import {
  AlertTriangle,
  ArrowLeft,
  BadgeCheck,
  BookOpen,
  BriefcaseBusiness,
  CalendarDays,
  CalendarX,
  Calculator,
  Clock3,
  Download,
  FileSpreadsheet,
  HelpCircle,
  KeyRound,
  LogIn,
  LogOut,
  Mail,
  MonitorCheck,
  RefreshCw,
  Search,
  Settings,
  ShieldCheck,
  UserPlus,
  Users
} from "lucide-react";
import { FormEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { businessDaysExcludingHolidaysInRange, formatClock, formatDate, formatMinutes, holidayForDate, isBusinessWeekday, minutesBetween, todayKey } from "../shared/calculations";
import type { Absence, AbsenceStatus, AttendanceDay, BootstrapData, Employee, JobTimeEntry, MonthlyClosure, Role } from "../shared/types";
import { JobTimesBackoffice } from "./job-times/JobTimesBackoffice";
import { TerminalJobTimePanel } from "./job-times/TerminalJobTimePanel";
import { TouchKeyboard } from "./TouchKeyboard";
import { RfidScanner } from "./rfidScanner";
import { TerminalPinForm, type TerminalPinFields } from "./TerminalPinForm";

type View = "terminal" | "portal" | "production" | "accounting" | "admin" | "system" | "help";

type AuthUser = {
  id: string;
  username: string;
  role: Role;
  employee_id: string | null;
};

type LoginResponse =
  | { user: AuthUser; requiresEmailConfirmation?: false }
  | { requiresEmailConfirmation: true; message: string };

type TerminalLookup = {
  terminalToken: string;
  authSource: "rfid" | "pin";
  pinConfigured: boolean;
  employee: Employee;
  day: AttendanceDay;
  status: "not_clocked_in" | "present" | "interrupted" | "clocked_out" | "absence" | "holiday" | "needs_review";
};

type TerminalJobSummaryPayload = {
  open_entry: JobTimeEntry | null;
  today_entries: JobTimeEntry[];
};

type ClockOutSummaryData = {
  day: AttendanceDay;
  jobMinutes: number | null;
};

type DashboardData = {
  employee: Employee;
  today: AttendanceDay;
  status?: TerminalLookup["status"];
  week: AttendanceDay[];
  month: AttendanceDay[];
  closure: MonthlyClosure;
  absences: Absence[];
};

type WeekOverviewDay = {
  date: string;
  day_status: AttendanceDay["day_status"];
  absence_label: string | null;
  first_clock_in: string | null;
  last_clock_out: string | null;
  net_work_minutes: number;
  job_minutes: number;
};

type WeekOverviewPayload = {
  year: number;
  week: number;
  dates: string[];
  rows: Array<{
    employee_id: string;
    short_code: string;
    display_name: string;
    days: WeekOverviewDay[];
    total_net_minutes: number;
    total_job_minutes: number;
  }>;
};

type ManagedAccessTarget = {
  key: string;
  label: string;
  username: string;
  email: string;
  role: Role;
  employee_number: string | null;
  employee_id: string | null;
  employee_missing: boolean;
  user: {
    id: string;
    username: string;
    email: string | null;
    role: Role;
    employee_id: string | null;
    active: boolean;
    must_change_password: boolean;
    updated_at: string;
  } | null;
};

const roleLabels: Record<Role, string> = {
  employee: "Mitarbeiter",
  production_lead: "Produktionsleitung",
  accounting: "Buchhaltung",
  admin: "Admin",
  system_admin: "Systemadmin"
};

const roleRank: Record<Role, number> = {
  employee: 1,
  production_lead: 2,
  accounting: 3,
  admin: 4,
  system_admin: 5
};

const statusLabels: Record<string, string> = {
  open: "offen",
  complete: "vollständig",
  missing_clock_in: "Kommen fehlt",
  missing_clock_out: "Gehen fehlt",
  absence: "Fehlzeit",
  holiday: "Feiertag",
  needs_review: "Prüfen",
  approved: "freigegeben",
  locked: "gesperrt",
  not_clocked_in: "nicht eingestempelt",
  present: "anwesend",
  interrupted: "unterbrochen",
  clocked_out: "ausgestempelt"
};

const absenceStatusLabels: Record<AbsenceStatus, string> = {
  planned: "geplant",
  approved: "genehmigt",
  confirmed: "bestätigt",
  cancelled: "storniert"
};

async function api<T>(url: string, options?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options?.headers ?? {})
    }
  });
  if (!response.ok) {
    const payload = await response.json().catch(() => ({ error: "Unbekannter Fehler" }));
    throw new Error(payload.error ?? "Anfrage fehlgeschlagen");
  }
  return response.json() as Promise<T>;
}

async function terminalApi<T>(url: string, body: unknown): Promise<T> {
  const controller = new AbortController();
  const timeout = window.setTimeout(() => controller.abort(), 15000);
  try {
    return await api<T>(url, { method: "POST", body: JSON.stringify(body), signal: controller.signal });
  } catch (error) {
    if (controller.signal.aborted || error instanceof TypeError) throw new Error("Keine Antwort vom Server. Bitte Verbindung prüfen und erneut anmelden, um den Buchungsstatus zu kontrollieren.");
    throw error;
  } finally { window.clearTimeout(timeout); }
}

function useAuth() {
  const [user, setUser] = useState<AuthUser | null>(null);

  async function refreshAuth() {
    const result = await api<{ user: AuthUser | null }>("/api/auth/me");
    setUser(result.user);
  }

  async function logout() {
    await api("/api/auth/logout", { method: "POST", body: "{}" });
    setUser(null);
  }

  useEffect(() => {
    void refreshAuth().catch(() => setUser(null));
  }, []);

  return { user, setUser, logout };
}

function IconButton({
  children,
  icon,
  onClick,
  variant = "secondary",
  type = "button",
  disabled = false
}: {
  children: string;
  icon: React.ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "danger" | "ghost";
  type?: "button" | "submit";
  disabled?: boolean;
}) {
  return (
    <button className={`button ${variant}`} disabled={disabled} onClick={onClick} type={type}>
      {icon}
      <span>{children}</span>
    </button>
  );
}

function StatusPill({ status, label }: { status: string; label?: string | null }) {
  return <span className={`pill ${status}`}>{label || statusLabels[status] || status}</span>;
}

function Metric({ label, value, tone = "neutral" }: { label: string; value: string; tone?: "neutral" | "good" | "warn" }) {
  return (
    <div className={`metric ${tone}`}>
      <span>{label}</span>
      <strong>{value}</strong>
    </div>
  );
}

function useBootstrap() {
  const [data, setData] = useState<BootstrapData | null>(null);
  const [error, setError] = useState<string | null>(null);

  async function refresh() {
    try {
      setError(null);
      setData(await api<BootstrapData>("/api/bootstrap"));
    } catch (err) {
      setError(err instanceof Error ? err.message : "Daten konnten nicht geladen werden.");
    }
  }

  useEffect(() => {
    void refresh();
  }, []);

  return { data, error, refresh };
}

export function App() {
  const { data, error, refresh } = useBootstrap();
  const auth = useAuth();
  const kioskMode = useMemo(() => new URLSearchParams(window.location.search).get("mode") === "kiosk", []);
  const initialPasswordResetToken = useMemo(() => new URLSearchParams(window.location.search).get("resetToken"), []);
  const [passwordResetToken, setPasswordResetToken] = useState(initialPasswordResetToken);
  const [view, setView] = useState<View>("terminal");
  const [helpReturnView, setHelpReturnView] = useState<Exclude<View, "help">>("terminal");

  const openHelp = useCallback(() => {
    if (view !== "help") {
      setHelpReturnView(view);
    }
    setView("help");
  }, [view]);

  if (passwordResetToken) {
    return (
      <div className="app">
        <PasswordResetPage
          token={passwordResetToken}
          onDone={() => {
            window.history.replaceState(null, "", window.location.pathname);
            setPasswordResetToken(null);
            setView("portal");
          }}
        />
      </div>
    );
  }

  if (error) {
    return (
      <main className="app error-state">
        <AlertTriangle size={48} />
        <h1>Daten konnten nicht geladen werden</h1>
        <p>{error}</p>
        <IconButton icon={<RefreshCw />} onClick={refresh} variant="primary">
          Erneut versuchen
        </IconButton>
      </main>
    );
  }

  if (!data) {
    return (
      <main className="app loading-state">
        <img src="/logo-horizontal.jpg" alt="DIALOGZENTRUM RHEIN-RUHR GmbH" />
        <p>Zeiterfassung wird gestartet...</p>
      </main>
    );
  }

  return (
    <div className={`app ${view === "terminal" ? "terminal-mode" : ""} ${kioskMode ? "kiosk-mode" : ""}`}>
      <header className="topbar">
        <button className="brand" onClick={() => setView("terminal")} title="Zum Terminal">
          <img src="/logo-horizontal.jpg" alt="DIALOGZENTRUM RHEIN-RUHR GmbH" />
        </button>
        {!kioskMode && (
          <>
            <nav className="main-nav" aria-label="Hauptnavigation">
              <button className={view === "terminal" ? "active" : ""} onClick={() => setView("terminal")}>
                <Clock3 /> Terminal
              </button>
              <button className={view === "portal" ? "active" : ""} onClick={() => setView("portal")}>
                <Users /> Portal
              </button>
              <button className={view === "production" ? "active" : ""} onClick={() => setView("production")}>
                <BriefcaseBusiness /> Produktion
              </button>
              <button className={view === "accounting" ? "active" : ""} onClick={() => setView("accounting")}>
                <FileSpreadsheet /> Buchhaltung
              </button>
              <button className={view === "admin" ? "active" : ""} onClick={() => setView("admin")}>
                <ShieldCheck /> Admin
              </button>
              <button className={view === "system" ? "active" : ""} onClick={() => setView("system")}>
                <Settings /> System
              </button>
              <button className={view === "help" ? "active" : ""} onClick={openHelp}>
                <HelpCircle /> Hilfe
              </button>
            </nav>
            <button className="refresh" onClick={() => void refresh()} title="Daten aktualisieren">
              <RefreshCw />
            </button>
          </>
        )}
      </header>

      {view === "terminal" && <TerminalView data={data} refresh={refresh} openHelp={openHelp} />}
      {view === "portal" && <EmployeePortal authUser={auth.user} onAuthChange={auth.setUser} onLogout={auth.logout} refresh={refresh} />}
      {view === "production" && <Backoffice authUser={auth.user} data={data} onAuthChange={auth.setUser} onLogout={auth.logout} refresh={refresh} role="production_lead" />}
      {view === "accounting" && <Backoffice authUser={auth.user} data={data} onAuthChange={auth.setUser} onLogout={auth.logout} refresh={refresh} role="accounting" />}
      {view === "admin" && <Backoffice authUser={auth.user} data={data} onAuthChange={auth.setUser} onLogout={auth.logout} refresh={refresh} role="admin" />}
      {view === "system" && <Backoffice authUser={auth.user} data={data} onAuthChange={auth.setUser} onLogout={auth.logout} refresh={refresh} role="system_admin" />}
      {view === "help" && <HelpManual onBack={() => setView(helpReturnView)} />}
      <TouchKeyboard enabled={kioskMode || view === "terminal"} />
    </div>
  );
}

function PasswordResetPage({ token, onDone }: { token: string; onDone: () => void }) {
  const [form, setForm] = useState({ password: "", repeat: "" });
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setMessage(null);
    if (form.password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (form.password !== form.repeat) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    setSaving(true);
    try {
      const result = await api<{ ok: boolean; message: string }>("/api/auth/password-reset/confirm", {
        method: "POST",
        body: JSON.stringify({ token, password: form.password })
      });
      setMessage(result.message);
      setDone(true);
      setForm({ password: "", repeat: "" });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passwort konnte nicht geändert werden.");
    } finally {
      setSaving(false);
    }
  }

  return (
    <main className="content employee-login">
      <section className="wide-panel password-reset-panel">
        <img src="/logo-horizontal.jpg" alt="DIALOGZENTRUM RHEIN-RUHR GmbH" />
        <h1>Neues Passwort vergeben</h1>
        <p>Bitte trage dein neues Passwort für die Zeiterfassung ein.</p>
        {!done && (
          <form className="password-reset-form" onSubmit={(event) => void submit(event)}>
            <label>
              Neues Passwort
              <input autoComplete="new-password" value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} type="password" />
            </label>
            <label>
              Passwort wiederholen
              <input autoComplete="new-password" value={form.repeat} onChange={(event) => setForm({ ...form, repeat: event.target.value })} type="password" />
            </label>
            <IconButton disabled={saving} icon={<KeyRound />} type="submit" variant="primary">
              {saving ? "Speichert" : "Passwort speichern"}
            </IconButton>
          </form>
        )}
        {message && <p className="form-success">{message}</p>}
        {error && <p className="form-error">{error}</p>}
        {done && (
          <IconButton icon={<LogIn />} onClick={onDone} variant="secondary">
            Zur Anmeldung
          </IconButton>
        )}
      </section>
    </main>
  );
}

function TerminalView({ data, refresh, openHelp }: { data: BootstrapData; refresh: () => Promise<void>; openHelp: () => void }) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [lookup, setLookup] = useState<TerminalLookup | null>(null);
  const scanner = useRef(new RfidScanner());
  const pending = useRef(false);
  const scanHandler = useRef<(uid: string) => void>(() => {});
  const [pinMode, setPinMode] = useState<"login" | "setup" | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [pinNotice, setPinNotice] = useState<string | null>(null);
  const [messageTone, setMessageTone] = useState<"success" | "error">("success");
  const [message, setMessage] = useState<string | null>(null);
  const [occasionMessage, setOccasionMessage] = useState<string | null>(null);
  const [showTodayDetails, setShowTodayDetails] = useState(false);
  const [jobMinutesToday, setJobMinutesToday] = useState<number | null>(null);
  const [clockOutSummary, setClockOutSummary] = useState<ClockOutSummaryData | null>(null);
  const [busy, setBusy] = useState(false);
  const [terminalNow, setTerminalNow] = useState(() => new Date(data.now));

  const focusRfidCapture = useCallback(() => {
    const active = document.activeElement;
    const typingInVisibleField =
      (active instanceof HTMLInputElement && active !== inputRef.current) ||
      active instanceof HTMLTextAreaElement ||
      active instanceof HTMLSelectElement ||
      (active instanceof HTMLElement && active.isContentEditable);

    if (!typingInVisibleField) {
      inputRef.current?.focus();
    }
  }, []);

  const resetTerminal = useCallback(
    (shouldRefresh = true) => {
      setMessage(null);
      setLookup(null);
      setOccasionMessage(null);
      setShowTodayDetails(false);
      setJobMinutesToday(null);
      setClockOutSummary(null);
      scanner.current.reset();
      setPinMode(null);
      setFormError(null);
      setPinNotice(null);
      setMessageTone("success");
      if (shouldRefresh) {
        void refresh();
      }
      window.setTimeout(focusRfidCapture, 0);
    },
    [focusRfidCapture, refresh]
  );

  useEffect(() => {
    if (pinMode) return;
    focusRfidCapture();
    const interval = window.setInterval(focusRfidCapture, 2500);
    const onKey = (event: KeyboardEvent) => {
      const target = event.target;
      if ((target instanceof HTMLInputElement && target !== inputRef.current) || target instanceof HTMLTextAreaElement || target instanceof HTMLSelectElement || (target instanceof HTMLElement && target.isContentEditable)) { scanner.current.reset(); return; }
      if (event.ctrlKey || event.altKey || event.metaKey || event.repeat) return;
      const uid = scanner.current.key(event.key, Date.now());
      if (event.key.length === 1 || uid) event.preventDefault();
      if (uid && !pending.current) scanHandler.current(uid);
    };
    const onFocus = () => { scanner.current.reset(); focusRfidCapture(); };
    document.addEventListener("keydown", onKey, true);
    window.addEventListener("focus", onFocus);
    return () => { window.clearInterval(interval); document.removeEventListener("keydown", onKey, true); window.removeEventListener("focus", onFocus); scanner.current.reset(); };
  }, [focusRfidCapture, pinMode]);

  useEffect(() => {
    const interval = window.setInterval(() => setTerminalNow(new Date()), 1000);
    return () => window.clearInterval(interval);
  }, []);

  useEffect(() => {
    if (!message || messageTone === "error") return;
    const timeout = window.setTimeout(() => {
      resetTerminal();
    }, clockOutSummary ? 20000 : occasionMessage ? 9000 : 3600);
    return () => window.clearTimeout(timeout);
  }, [clockOutSummary, message, messageTone, occasionMessage, resetTerminal]);

  useEffect(() => {
    if ((!lookup && !pinMode) || message || busy) return;
    let timer = 0;
    const timeoutMs = pinMode ? 90000 : 30000;
    const scheduleReset = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => resetTerminal(), timeoutMs);
    };
    scheduleReset();
    window.addEventListener("pointerdown", scheduleReset);
    window.addEventListener("keydown", scheduleReset);
    window.addEventListener("touchstart", scheduleReset);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener("pointerdown", scheduleReset);
      window.removeEventListener("keydown", scheduleReset);
      window.removeEventListener("touchstart", scheduleReset);
    };
  }, [busy, lookup, pinMode, message, resetTerminal]);

  async function loadJobMinutes(employeeId: string, day?: AttendanceDay): Promise<number> {
    const payload = await api<TerminalJobSummaryPayload>(`/api/job-times/terminal/${employeeId}`);
    return pauseAdjustedJobMinutes(day, sumJobTimeMinutes(payload.today_entries));
  }

  useEffect(() => {
    if (!lookup || message) {
      if (!lookup) setJobMinutesToday(null);
      return;
    }
    let cancelled = false;
    void loadJobMinutes(lookup.employee.id, lookup.day)
      .then((minutes) => {
        if (!cancelled) setJobMinutesToday(minutes);
      })
      .catch(() => {
        if (!cancelled) setJobMinutesToday(null);
      });
    return () => {
      cancelled = true;
    };
  }, [lookup?.employee.id, lookup?.status, message]);

  async function lookupEmployee(body: { rfidUid?: string; shortCode?: string; pin?: string }) {
    if (pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      setMessage(null);
      setLookup(null);
      setFormError(null);
      setPinNotice(null);
      setOccasionMessage(null);
      setShowTodayDetails(false);
      setClockOutSummary(null);
      setLookup(await terminalApi<TerminalLookup>("/api/terminal/lookup", body));
      setPinMode(null);
    } catch (err) {
      const error = err instanceof Error ? err.message : "Anmeldung fehlgeschlagen.";
      if (body.rfidUid) { setMessageTone("error"); setMessage(error); }
      else setFormError(error);
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }
  scanHandler.current = (uid) => { void lookupEmployee({ rfidUid: uid }); };

  async function submitPinForm(fields: TerminalPinFields) {
    if (pinMode === "login") { await lookupEmployee({ shortCode: fields.shortCode, pin: fields.pin }); return; }
    if (!lookup || pending.current) return;
    if (fields.pin !== fields.repeatPin) { setFormError("Die beiden PIN-Eingaben stimmen nicht überein."); return; }
    pending.current = true;
    setBusy(true);
    setFormError(null);
    try {
      const result = await terminalApi<{ terminalToken: string }>("/api/terminal/pin", { employeeId: lookup.employee.id, terminalToken: lookup.terminalToken, pin: fields.pin, repeatPin: fields.repeatPin, currentPin: fields.currentPin });
      setLookup({ ...lookup, terminalToken: result.terminalToken, pinConfigured: true });
      setPinMode(null);
      setPinNotice(`PIN gespeichert. Künftig können Sie sich mit ${lookup.employee.short_code} und Ihrer PIN anmelden. Arbeitsbeginn und Arbeitsende bitte wie gewohnt buchen.`);
    } catch (err) { setFormError(err instanceof Error ? err.message : "PIN konnte nicht gespeichert werden."); }
    finally { pending.current = false; setBusy(false); }
  }

  function openPinLogin() { resetTerminal(false); setPinMode("login"); }

  async function clock(type: "clock_in" | "clock_out" | "break_start" | "break_end") {
    if (!lookup || pending.current) return;
    pending.current = true;
    setBusy(true);
    try {
      const updated = await terminalApi<TerminalLookup>("/api/attendance/clock", { employeeId: lookup.employee.id, type, terminalToken: lookup.terminalToken, terminalId: data.terminals[0]?.id });
      setLookup({ ...lookup, ...updated });
      setMessageTone("success");
      const messages = {
        clock_in: "Arbeitsbeginn gespeichert.",
        break_start: "Unterbrechung gespeichert.",
        break_end: "Arbeit wieder aufgenommen.",
        clock_out: "Arbeitsende gespeichert."
      };
      setOccasionMessage(terminalOccasionMessage(type, updated.day, updated.employee.id));
      if (type === "clock_out") {
        const jobMinutes = await loadJobMinutes(updated.employee.id, updated.day).catch(() => jobMinutesToday);
        setJobMinutesToday(jobMinutes);
        setClockOutSummary({ day: updated.day, jobMinutes });
      } else {
        setClockOutSummary(null);
      }
      setMessage(messages[type]);
    } catch (err) {
      setMessageTone("error");
      setMessage(err instanceof Error ? err.message : "Buchung fehlgeschlagen.");
    } finally {
      pending.current = false;
      setBusy(false);
    }
  }

  const clockNow = new Intl.DateTimeFormat("de-DE", { hour: "2-digit", minute: "2-digit" }).format(terminalNow);
  const currentDay = lookup?.day;

  return (
    <main className="terminal-screen">
      <input
        ref={inputRef}
        className="rfid-capture"
        readOnly
        tabIndex={-1}
        aria-label="RFID Eingabefeld"
        autoFocus
      />

      {pinMode && <TerminalPinForm key={`${pinMode}-${lookup?.employee.id ?? ""}`} mode={pinMode} name={lookup?.employee.display_name} shortCode={lookup?.employee.short_code} configured={lookup?.pinConfigured} busy={busy} error={formError} onSubmit={submitPinForm} onCancel={() => { setPinMode(null); setFormError(null); if (!lookup) resetTerminal(false); }} />}

      {!lookup && !message && !pinMode && (
        <section className="terminal-standby">
          <img src="/logo-vertical.png" alt="DIALOGZENTRUM RHEIN-RUHR GmbH" />
          <div className="rfid-symbol">
            <BadgeCheck size={88} />
          </div>
          <h1>Bitte RFID-Karte vorhalten</h1>
          <p>{formatDate(todayKey(terminalNow))} · {clockNow} Uhr · {data.terminals[0]?.name}</p>
          <div className="terminal-reminder" role="note">
            <Clock3 />
            <span>Erst bei Arbeitsantritt einbuchen, nach Arbeitsende direkt ausbuchen!</span>
          </div>
          <div className="terminal-actions">
            <IconButton icon={<KeyRound />} onClick={openPinLogin} variant="primary" disabled={busy}>Ohne Karte anmelden</IconButton>
            <IconButton icon={<RefreshCw />} onClick={() => resetTerminal(true)} variant="secondary">
              Aktualisieren
            </IconButton>
            <IconButton icon={<HelpCircle />} onClick={openHelp} variant="ghost">
              Hilfe
            </IconButton>
          </div>
        </section>
      )}

      {message && !pinMode && (
        <section role={messageTone === "error" ? "alert" : "status"} className={`terminal-result ${messageTone === "error" ? "terminal-error" : "terminal-success"}${clockOutSummary ? " clockout-result" : ""}`}>
          {messageTone === "error" ? <AlertTriangle size={72} /> : <BadgeCheck size={72} />}
          <h1>{message}</h1>
          {occasionMessage && <div className="terminal-occasion">{occasionMessage}</div>}
          {messageTone === "error" ? <><p>Bitte die Karte erneut vorhalten oder mit Kürzel und PIN anmelden. Prüfen Sie danach den angezeigten Buchungsstatus.</p><div className="terminal-actions"><IconButton icon={<RefreshCw />} onClick={() => resetTerminal(true)}>Zurück / Erneut versuchen</IconButton><IconButton icon={<KeyRound />} onClick={openPinLogin} variant="primary">Ohne Karte anmelden</IconButton></div></> : clockOutSummary ? (
            <ClockOutSummaryCard day={clockOutSummary.day} jobMinutes={clockOutSummary.jobMinutes} />
          ) : (
            <>
              {lookup && <p>{lookup.employee.short_code} · {lookup.employee.display_name}</p>}
              <p>Das Terminal kehrt gleich zum Startbildschirm zurück.</p>
            </>
          )}
        </section>
      )}

      {lookup && !message && !pinMode && (
        <section className="terminal-card">
          <div className="terminal-person">
            <div>
              <p>Hallo {lookup.employee.short_code}</p>
              <h1>{lookup.employee.display_name}</h1>
            </div>
            <StatusPill status={lookup.status} />
          </div>
          {pinNotice && <p className="terminal-pin-notice" role="status">{pinNotice}</p>}
          {lookup.authSource === "rfid" && <div className="terminal-pin-offer"><p>{lookup.pinConfigured ? "Ihre persönliche PIN ist eingerichtet." : "Einmal einrichten: Bei Kartenproblemen künftig mit Kürzel und eigener PIN anmelden."}</p><IconButton icon={<KeyRound />} disabled={busy} onClick={() => { setPinMode("setup"); setFormError(null); }} variant="secondary">{lookup.pinConfigured ? "Eigene PIN ändern" : "Eigene PIN einrichten"}</IconButton></div>}

          <>
              <div className="metrics-grid">
                <Metric label="Kommen" value={formatClock(currentDay?.first_clock_in ?? null)} />
                <Metric label="Gehen" value={formatClock(currentDay?.last_clock_out ?? null)} />
                <Metric label="Netto" value={formatMinutes(currentDay?.net_work_minutes ?? 0)} />
                <Metric label="Soll" value={formatMinutes(currentDay?.target_minutes ?? 0)} />
                <Metric label="Saldo" value={formatMinutes(currentDay?.balance_minutes ?? 0, true)} tone={(currentDay?.balance_minutes ?? 0) >= 0 ? "good" : "warn"} />
                <Metric label="Pause" value={formatMinutes(currentDay?.deducted_break_minutes ?? 0)} />
              </div>

              <div className="terminal-actions">
                {lookup.status === "not_clocked_in" && (
                  <IconButton icon={<LogIn />} onClick={() => void clock("clock_in")} variant="primary">
                    Arbeitsbeginn buchen
                  </IconButton>
                )}
                {lookup.status === "present" && (
                  <IconButton icon={<RefreshCw />} onClick={() => void clock("break_start")} variant="secondary">
                    Unterbrechung buchen
                  </IconButton>
                )}
                {lookup.status === "present" && (
                  <IconButton icon={<LogOut />} onClick={() => void clock("clock_out")} variant="primary">
                    Arbeitsende buchen
                  </IconButton>
                )}
                {lookup.status === "interrupted" && (
                  <IconButton icon={<LogIn />} onClick={() => void clock("break_end")} variant="primary">
                    Arbeit wieder aufnehmen
                  </IconButton>
                )}
                {lookup.status === "interrupted" && (
                  <IconButton icon={<LogOut />} onClick={() => void clock("clock_out")} variant="secondary">
                    Arbeitsende buchen
                  </IconButton>
                )}
                {lookup.status === "needs_review" && (
                  <p className="form-error">
                    Diese Buchung braucht eine Prüfung im Backoffice. Bitte wende dich an die Buchhaltung.
                  </p>
                )}
                {lookup.status === "clocked_out" && (
                  <p className="form-success">
                    Der Arbeitstag ist bereits abgeschlossen.
                  </p>
                )}
                <IconButton icon={<CalendarDays />} onClick={() => setShowTodayDetails((current) => !current)} variant="secondary">
                  {showTodayDetails ? "Zeiten ausblenden" : "Meine Zeiten heute"}
                </IconButton>
                <IconButton icon={<HelpCircle />} onClick={openHelp} variant="ghost">
                  Hilfe
                </IconButton>
              <IconButton icon={<RefreshCw />} onClick={() => resetTerminal(false)} variant="ghost">
                Abbrechen
              </IconButton>
              </div>
              {showTodayDetails && currentDay && (
                <div className="today-details" aria-live="polite">
                  <h2>Meine Zeiten heute</h2>
                  <div className="confirm-grid">
                    <span>Arbeitsbeginn</span>
                    <strong>{formatClock(currentDay.first_clock_in)}</strong>
                    <span>Letztes Arbeitsende</span>
                    <strong>{formatClock(currentDay.last_clock_out)}</strong>
                    <span>Arbeitszeit brutto</span>
                    <strong>{formatMinutes(currentDay.gross_minutes)}</strong>
                    <span>Automatische Pause</span>
                    <strong>{formatMinutes(currentDay.deducted_break_minutes)}</strong>
                    <span>Nettozeit</span>
                    <strong>{formatMinutes(currentDay.net_work_minutes)}</strong>
                    <span>Tagessaldo</span>
                    <strong>{formatMinutes(currentDay.balance_minutes, true)}</strong>
                  </div>
                </div>
              )}
              {(lookup.status === "present" || lookup.status === "interrupted") && (
                <TerminalJobTimePanel employeeId={lookup.employee.id} terminalStatus={lookup.status} />
              )}
            </>

          {busy && <p className="busy">Bitte warten...</p>}
        </section>
      )}
    </main>
  );
}

const mondayStartMessages = [
  "Wir wünschen einen guten Start in die Woche!",
  "Schön, dass Sie da sind. Einen guten Wochenstart!",
  "Wir wünschen eine gute und erfolgreiche neue Woche."
];

const fridayWeekendMessages = [
  "Wir wünschen einen schönen Feierabend und ein erholsames Wochenende!",
  "Vielen Dank für die Woche. Kommen Sie gut ins Wochenende!",
  "Schönes Wochenende und gute Erholung!"
];

function terminalOccasionMessage(type: "clock_in" | "clock_out" | "break_start" | "break_end", day: AttendanceDay, employeeId: string): string | null {
  const timestamp = type === "clock_out" ? day.last_clock_out : day.first_clock_in;
  if (!timestamp) return null;
  const date = new Date(timestamp);
  const weekday = date.getDay();
  if (type === "clock_in" && weekday === 1) return rotatingTerminalMessage(mondayStartMessages, employeeId, day.date, type);
  if (type === "clock_out" && weekday === 5) return rotatingTerminalMessage(fridayWeekendMessages, employeeId, day.date, type);
  return null;
}

function rotatingTerminalMessage(messages: string[], employeeId: string, date: string, type: string): string {
  const seed = `${employeeId}-${date}-${type}`;
  const index = Array.from(seed).reduce((sum, char) => sum + char.charCodeAt(0), 0) % messages.length;
  return messages[index];
}

function sumJobTimeMinutes(entries: JobTimeEntry[]): number {
  const now = new Date().toISOString();
  return entries.reduce((sum, entry) => {
    if (entry.status === "running" && entry.start_time) {
      return sum + minutesBetween(entry.start_time, now);
    }
    return sum + entry.duration_minutes;
  }, 0);
}

function pauseAdjustedJobMinutes(day: AttendanceDay | undefined, rawJobMinutes: number): number {
  if (!day) return rawJobMinutes;
  const unassignedBeforePause = Math.max(0, day.gross_minutes - rawJobMinutes);
  const jobPauseDeduction = Math.max(0, day.deducted_break_minutes - unassignedBeforePause);
  return Math.max(0, rawJobMinutes - jobPauseDeduction);
}

function ClockOutSummaryCard({
  day,
  jobMinutes,
  clockOutLabel
}: {
  day: AttendanceDay;
  jobMinutes: number | null;
  clockOutLabel?: string;
}) {
  const difference = jobMinutes === null ? null : day.net_work_minutes - jobMinutes;
  const differenceTone = difference === null ? "" : difference === 0 ? " good" : " warn";
  const differenceText =
    difference === null
      ? "-"
      : difference === 0
        ? "voll zugeordnet"
        : difference > 0
          ? "noch nicht in Auftragszeit gebucht"
          : "mehr Auftragszeit als Nettozeit";

  return (
    <div className="clockout-summary" aria-label="Arbeitsende Zusammenfassung">
      <div className="clockout-summary-row">
        <span>Eingestempelt um</span>
        <strong>{formatClock(day.first_clock_in)}</strong>
      </div>
      <div className="clockout-summary-row">
        <span>Ausgestempelt um</span>
        <strong>{day.last_clock_out ? formatClock(day.last_clock_out) : (clockOutLabel ?? "-")}</strong>
      </div>
      <div className="clockout-summary-row">
        <span>Arbeitszeit netto</span>
        <strong>{formatMinutes(day.net_work_minutes)}</strong>
      </div>
      <div className="clockout-summary-row">
        <span>davon in Auftragszeit gebucht</span>
        <strong>{jobMinutes === null ? "-" : formatMinutes(jobMinutes)}</strong>
      </div>
      <div className={`clockout-summary-row clockout-summary-difference${differenceTone}`}>
        <span>Differenz</span>
        <strong>{difference === null ? "-" : formatMinutes(difference, true)}</strong>
      </div>
      <p className={`clockout-summary-note${differenceTone}`}>{differenceText}</p>
    </div>
  );
}

function EmployeePortal({
  authUser,
  onAuthChange,
  onLogout,
  refresh
}: {
  authUser: AuthUser | null;
  onAuthChange: (user: AuthUser | null) => void;
  onLogout: () => Promise<void>;
  refresh: () => Promise<void>;
}) {
  const [dashboard, setDashboard] = useState<DashboardData | null>(null);
  const [section, setSection] = useState<"dashboard" | "month" | "closures" | "help">("dashboard");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function loadDashboard() {
    setError(null);
    setDashboard(await api<DashboardData>("/api/employee-portal/dashboard"));
  }

  useEffect(() => {
    if (!authUser || roleRank[authUser.role] < roleRank.employee) {
      setDashboard(null);
      return;
    }
    void loadDashboard().catch((err) => setError(err instanceof Error ? err.message : "Mitarbeiterdaten konnten nicht geladen werden."));
  }, [authUser?.id]);

  async function clock(type: "clock_in" | "clock_out" | "break_start" | "break_end") {
    setBusy(true);
    setError(null);
    try {
      await api<TerminalLookup>("/api/employee-portal/clock", {
        method: "POST",
        body: JSON.stringify({ type })
      });
      await loadDashboard();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Buchung fehlgeschlagen.");
    } finally {
      setBusy(false);
    }
  }

  if (!authUser || roleRank[authUser.role] < roleRank.employee) {
    return <BackofficeLogin onAuthChange={onAuthChange} refresh={refresh} requiredRole="employee" />;
  }

  if (!authUser.employee_id) {
    return (
      <main className="content employee-login">
        <section className="wide-panel">
          <h1>Mitarbeiterportal</h1>
          <p className="form-error">Dieser Benutzer ist noch keinem Mitarbeiter zugeordnet.</p>
          <IconButton icon={<LogOut />} onClick={() => void onLogout()} variant="secondary">
            Abmelden
          </IconButton>
        </section>
      </main>
    );
  }

  if (!dashboard) {
    return (
      <main className="content employee-login">
        <section className="wide-panel">
          <h1>Mitarbeiterportal</h1>
          <p>{error ?? "Mitarbeiterdaten werden geladen..."}</p>
          <div className="terminal-actions">
            <IconButton icon={<RefreshCw />} onClick={() => void loadDashboard()} variant="secondary">
              Aktualisieren
            </IconButton>
            <IconButton icon={<LogOut />} onClick={() => void onLogout()} variant="ghost">
              Abmelden
            </IconButton>
          </div>
        </section>
      </main>
    );
  }

  const employee = dashboard.employee;
  const status = dashboard.status ?? "not_clocked_in";

  return (
    <main className="content">
      <div className="section-heading">
        <div>
          <p>Mitarbeiterportal</p>
          <h1>Hallo {employee.short_code}</h1>
        </div>
        <div className="terminal-actions">
          <IconButton icon={<RefreshCw />} onClick={() => void loadDashboard()} variant="secondary">
            Aktualisieren
          </IconButton>
          <IconButton icon={<LogOut />} onClick={() => void onLogout()} variant="ghost">
            Abmelden
          </IconButton>
        </div>
      </div>
      <div className="subnav">
        <button className={section === "dashboard" ? "active" : ""} onClick={() => setSection("dashboard")}>
          Dashboard
        </button>
        <button className={section === "month" ? "active" : ""} onClick={() => setSection("month")}>
          Monatsübersicht
        </button>
        <button className={section === "closures" ? "active" : ""} onClick={() => setSection("closures")}>
          Monatsabschlüsse
        </button>
        <button className={section === "help" ? "active" : ""} onClick={() => setSection("help")}>
          Hilfe
        </button>
      </div>

      {section === "dashboard" && (
        <>
          <section className="wide-panel remote-clock-panel">
            <div>
              <h2>Stempeln</h2>
              <StatusPill status={status} />
            </div>
            <div className="terminal-actions">
              {status === "not_clocked_in" && (
                <IconButton disabled={busy} icon={<LogIn />} onClick={() => void clock("clock_in")} variant="primary">
                  Arbeitsbeginn buchen
                </IconButton>
              )}
              {status === "present" && (
                <IconButton disabled={busy} icon={<RefreshCw />} onClick={() => void clock("break_start")} variant="secondary">
                  Unterbrechung buchen
                </IconButton>
              )}
              {status === "present" && (
                <IconButton disabled={busy} icon={<LogOut />} onClick={() => void clock("clock_out")} variant="primary">
                  Arbeitsende buchen
                </IconButton>
              )}
              {status === "interrupted" && (
                <IconButton disabled={busy} icon={<LogIn />} onClick={() => void clock("break_end")} variant="primary">
                  Arbeit wieder aufnehmen
                </IconButton>
              )}
              {status === "interrupted" && (
                <IconButton disabled={busy} icon={<LogOut />} onClick={() => void clock("clock_out")} variant="secondary">
                  Arbeitsende buchen
                </IconButton>
              )}
            </div>
            {error && <p className="form-error">{error}</p>}
          </section>
          <div className="metrics-grid employee-metrics">
            <Metric label="Heute Kommen" value={formatClock(dashboard.today.first_clock_in)} />
            <Metric label="Heute Gehen" value={formatClock(dashboard.today.last_clock_out)} />
            <Metric label="Netto heute" value={formatMinutes(dashboard.today.net_work_minutes)} />
            <Metric label="Saldo heute" value={formatMinutes(dashboard.today.balance_minutes, true)} tone={dashboard.today.balance_minutes >= 0 ? "good" : "warn"} />
            <Metric label="Monat Soll" value={formatMinutes(dashboard.closure.target_minutes)} />
            <Metric label="Kontostand" value={formatMinutes(dashboard.closure.carryover_new_minutes, true)} tone={dashboard.closure.carryover_new_minutes >= 0 ? "good" : "warn"} />
          </div>
          <DayTable days={dashboard.week} title="Diese Woche" />
        </>
      )}

      {section === "month" && <DayTable days={dashboard.month} title="Monatsübersicht" />}
      {section === "closures" && <ClosureSummary closure={dashboard.closure} employee={employee} />}
      {section === "help" && <EmployeeHelp />}
    </main>
  );
}

function Backoffice({
  authUser,
  data,
  onAuthChange,
  onLogout,
  refresh,
  role
}: {
  authUser: AuthUser | null;
  data: BootstrapData;
  onAuthChange: (user: AuthUser | null) => void;
  onLogout: () => Promise<void>;
  refresh: () => Promise<void>;
  role: Role;
}) {
  const productionOnly = role === "production_lead";
  const [section, setSection] = useState<
    "today" | "week" | "jobtimes" | "employees" | "corrections" | "absences" | "closures" | "holidays" | "users" | "passwords" | "terminals" | "exports" | "audit" | "system"
  >(
    productionOnly ? "jobtimes" : "today"
  );
  const allowedSystem = role === "system_admin";
  const title = roleLabels[role];
  const requiredRole = role;

  if (!authUser || roleRank[authUser.role] < roleRank[requiredRole]) {
    return <BackofficeLogin onAuthChange={onAuthChange} refresh={refresh} requiredRole={requiredRole} />;
  }

  const canAdmin = roleRank[authUser.role] >= roleRank.admin;

  return (
    <main className="content">
      <div className="section-heading">
        <div>
          <p>{title}</p>
          <h1>{role === "production_lead" ? "Auftragszeiten Produktion" : role === "accounting" ? "Zeitdaten und Monatsabschluss" : role === "admin" ? "Administration" : "Systemstatus"}</h1>
        </div>
        <div className="header-actions">
          <span className="signed-in">
            {authUser.username} · {roleLabels[authUser.role]}
          </span>
          <IconButton icon={<RefreshCw />} onClick={() => void refresh()} variant="secondary">
            Aktualisieren
          </IconButton>
          <IconButton
            icon={<LogOut />}
            onClick={() => {
              void onLogout().then(refresh);
            }}
            variant="ghost"
          >
            Abmelden
          </IconButton>
        </div>
      </div>
      <div className="subnav">
        {!productionOnly && (
          <>
        <button className={section === "today" ? "active" : ""} onClick={() => setSection("today")}>
          Tagesübersicht
        </button>
        <button className={section === "week" ? "active" : ""} onClick={() => setSection("week")}>
          Wochenübersicht
        </button>
        <button className={section === "employees" ? "active" : ""} onClick={() => setSection("employees")}>
          Mitarbeiter
        </button>
        <button className={section === "corrections" ? "active" : ""} onClick={() => setSection("corrections")}>
          Korrekturen
        </button>
        <button className={section === "absences" ? "active" : ""} onClick={() => setSection("absences")}>
          Fehlzeiten
        </button>
        <button className={section === "closures" ? "active" : ""} onClick={() => setSection("closures")}>
          Monatsabschluss
        </button>
          </>
        )}
        <button className={section === "jobtimes" ? "active" : ""} onClick={() => setSection("jobtimes")}>
          Auftragszeiten
        </button>
        {canAdmin && (
          <button className={section === "holidays" ? "active" : ""} onClick={() => setSection("holidays")}>
            Feiertage
          </button>
        )}
        {canAdmin && (
          <button className={section === "users" ? "active" : ""} onClick={() => setSection("users")}>
            Benutzer
          </button>
        )}
        {canAdmin && (
          <button className={section === "passwords" ? "active" : ""} onClick={() => setSection("passwords")}>
            Passwörter
          </button>
        )}
        {canAdmin && (
          <button className={section === "terminals" ? "active" : ""} onClick={() => setSection("terminals")}>
            Terminals
          </button>
        )}
        {!productionOnly && (
          <>
        <button className={section === "exports" ? "active" : ""} onClick={() => setSection("exports")}>
          Exporte
        </button>
        <button className={section === "audit" ? "active" : ""} onClick={() => setSection("audit")}>
          Audit-Log
        </button>
          </>
        )}
        {allowedSystem && (
          <button className={section === "system" ? "active" : ""} onClick={() => setSection("system")}>
            System
          </button>
        )}
      </div>

      {section === "today" && !productionOnly && <TodayOverview data={data} />}
      {section === "week" && !productionOnly && <WeekOverview />}
      {section === "jobtimes" && <JobTimesBackoffice data={data} refresh={refresh} />}
      {section === "employees" && !productionOnly && <EmployeeAdmin data={data} refresh={refresh} role={role} />}
      {section === "corrections" && !productionOnly && <CorrectionsAdmin data={data} refresh={refresh} />}
      {section === "absences" && !productionOnly && <AbsenceAdmin data={data} refresh={refresh} />}
      {section === "closures" && !productionOnly && <ClosureAdmin data={data} refresh={refresh} canLock={role !== "accounting"} />}
      {section === "holidays" && <HolidayAdmin data={data} refresh={refresh} />}
      {section === "users" && <UsersAdmin data={data} refresh={refresh} />}
      {section === "passwords" && <ManagedPasswordsAdmin refresh={refresh} />}
      {section === "terminals" && <TerminalAdmin data={data} refresh={refresh} />}
      {section === "exports" && !productionOnly && <ExportPanel />}
      {section === "audit" && !productionOnly && <AuditPanel data={data} />}
      {section === "system" && <SystemPanel />}
    </main>
  );
}

function BackofficeLogin({
  onAuthChange,
  refresh,
  requiredRole
}: {
  onAuthChange: (user: AuthUser | null) => void;
  refresh: () => Promise<void>;
  requiredRole: Role;
}) {
  const [form, setForm] = useState({ username: "", password: "" });
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetEmail, setResetEmail] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [resetMessage, setResetMessage] = useState<string | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setInfo(null);
    try {
      const result = await api<LoginResponse>("/api/auth/login", {
        method: "POST",
        body: JSON.stringify(form)
      });
      if ("requiresEmailConfirmation" in result && result.requiresEmailConfirmation) {
        setInfo(result.message);
        return;
      }
      onAuthChange(result.user);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Anmeldung fehlgeschlagen.");
      setResetOpen(true);
      if (form.username.includes("@")) {
        setResetEmail(form.username);
      }
    }
  }

  async function requestPasswordReset(event: FormEvent) {
    event.preventDefault();
    setResetBusy(true);
    setResetError(null);
    setResetMessage(null);
    try {
      const result = await api<{ ok: boolean; message: string }>("/api/auth/password-reset/request", {
        method: "POST",
        body: JSON.stringify({ email: resetEmail })
      });
      setResetMessage(result.message);
    } catch (err) {
      setResetError(err instanceof Error ? err.message : "Reset-Link konnte nicht versendet werden.");
    } finally {
      setResetBusy(false);
    }
  }

  return (
    <main className="content employee-login">
      <section className="wide-panel">
        <h1>{roleLabels[requiredRole]} Anmeldung</h1>
        <p>Für diesen Bereich ist eine passende Rolle erforderlich.</p>
        <form className="login-form" onSubmit={(event) => void submit(event)}>
          <label>
            Benutzername
            <input value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
          </label>
          <label>
            Passwort
            <input value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} type="password" />
          </label>
          <IconButton icon={<KeyRound />} type="submit" variant="primary">
            Anmelden
          </IconButton>
        </form>
        <p className="hint">Externe Anmeldungen müssen per E-Mail bestätigt werden.</p>
        <button
          className="link-button"
          onClick={() => {
            setResetOpen((current) => !current);
            if (!resetEmail && form.username.includes("@")) setResetEmail(form.username);
          }}
          type="button"
        >
          Passwort vergessen oder falsch eingegeben?
        </button>
        {resetOpen && (
          <form className="password-forgot-form" onSubmit={(event) => void requestPasswordReset(event)}>
            <label>
              E-Mail-Adresse
              <input
                autoComplete="email"
                placeholder="o.gremmer@dzr-r.de"
                type="email"
                value={resetEmail}
                onChange={(event) => setResetEmail(event.target.value)}
              />
            </label>
            <IconButton disabled={resetBusy} icon={<Mail />} type="submit" variant="secondary">
              {resetBusy ? "Sendet" : "Reset-Link senden"}
            </IconButton>
          </form>
        )}
        {resetMessage && <p className="form-success">{resetMessage}</p>}
        {resetError && <p className="form-error">{resetError}</p>}
        {info && <p className="form-success">{info}</p>}
        {error && <p className="form-error">{error}</p>}
      </section>
    </main>
  );
}

function TodayOverview({ data }: { data: BootstrapData }) {
  const dayByEmployee = new Map(data.attendance_days.map((day) => [day.employee_id, day]));
  return (
    <section className="wide-panel">
      <h2>Tagesübersicht {formatDate(data.today)}</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th>Status</th>
              <th>Kommen</th>
              <th>Gehen</th>
              <th>Brutto</th>
              <th>Pause</th>
              <th>Netto</th>
              <th>Soll</th>
              <th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {data.employees.map((employee) => {
              const day = dayByEmployee.get(employee.id);
              return (
                <tr key={employee.id}>
                  <td>
                    <strong>{employee.short_code}</strong> {employee.display_name}
                  </td>
                  <td>{day && <StatusPill label={day.day_status === "absence" ? day.absence_label : null} status={day.day_status} />}</td>
                  <td>{formatClock(day?.first_clock_in ?? null)}</td>
                  <td>{formatClock(day?.last_clock_out ?? null)}</td>
                  <td>{formatMinutes(day?.gross_minutes ?? 0)}</td>
                  <td>{formatMinutes(day?.deducted_break_minutes ?? 0)}</td>
                  <td>{formatMinutes(day?.net_work_minutes ?? 0)}</td>
                  <td>{formatMinutes(day?.target_minutes ?? 0)}</td>
                  <td className={(day?.balance_minutes ?? 0) >= 0 ? "positive" : "negative"}>{formatMinutes(day?.balance_minutes ?? 0, true)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <div className="alerts">
        {data.attendance_days
          .filter((day) => ["missing_clock_in", "missing_clock_out", "needs_review"].includes(day.day_status))
          .map((day) => (
            <p key={day.id}>
              <AlertTriangle size={18} /> {data.employees.find((employee) => employee.id === day.employee_id)?.short_code}: {statusLabels[day.day_status]}
            </p>
          ))}
      </div>
    </section>
  );
}

function WeekOverview() {
  const initialWeek = useMemo(() => currentIsoWeek(), []);
  const [filters, setFilters] = useState({ year: initialWeek.year, week: initialWeek.week });
  const [payload, setPayload] = useState<WeekOverviewPayload | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  async function load(year = filters.year, week = filters.week) {
    setLoading(true);
    setMessage(null);
    try {
      setPayload(await api<WeekOverviewPayload>(`/api/attendance/week-overview?year=${year}&week=${week}`));
    } catch (error) {
      setMessage(error instanceof Error ? error.message : "Wochenübersicht konnte nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    void load(initialWeek.year, initialWeek.week);
  }, []);

  function submit(event: FormEvent) {
    event.preventDefault();
    void load();
  }

  return (
    <section className="wide-panel weekly-overview">
      <div className="weekly-heading">
        <div>
          <h2>Wochenübersicht {payload ? `KW ${payload.week}/${payload.year}` : ""}</h2>
          <p>Einstempeln, Ausstempeln und Auftragszeit je Tag.</p>
        </div>
        <form className="week-picker" onSubmit={submit}>
          <label>
            Jahr
            <input type="number" min="2020" max="2100" value={filters.year} onChange={(event) => setFilters({ ...filters, year: Number(event.target.value) })} />
          </label>
          <label>
            KW
            <input type="number" min="1" max="53" value={filters.week} onChange={(event) => setFilters({ ...filters, week: Number(event.target.value) })} />
          </label>
          <IconButton disabled={loading} icon={<RefreshCw />} type="submit" variant="secondary">
            Anzeigen
          </IconButton>
        </form>
      </div>

      {message && <p className="form-error">{message}</p>}
      {!payload && !message && <p className="hint">Wochenübersicht wird geladen...</p>}
      {payload && (
        <div className="table-wrap week-table-wrap">
          <table className="week-table">
            <thead>
              <tr>
                <th>Mitarbeiter</th>
                {payload.dates.map((date) => (
                  <th key={date}>{formatWeekdayHeader(date)}</th>
                ))}
                <th>Summe</th>
              </tr>
            </thead>
            <tbody>
              {payload.rows.map((row) => (
                <tr key={row.employee_id}>
                  <td className="week-employee">
                    <strong>{row.short_code}</strong>
                    <span>{row.display_name}</span>
                  </td>
                  {row.days.map((day) => (
                    <td key={day.date}>
                      <WeekDayCell day={day} />
                    </td>
                  ))}
                  <td className="week-total">
                    <span>Netto {formatMinutes(row.total_net_minutes)}</span>
                    <strong>Auftrag {formatMinutes(row.total_job_minutes)}</strong>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function WeekDayCell({ day }: { day: WeekOverviewDay }) {
  const hasTime = Boolean(day.first_clock_in || day.last_clock_out || day.job_minutes || day.net_work_minutes);
  if (!hasTime && (day.day_status === "holiday" || day.day_status === "absence")) {
    return (
      <div className="week-cell quiet">
        <StatusPill label={day.day_status === "absence" ? day.absence_label : null} status={day.day_status} />
      </div>
    );
  }
  return (
    <div className={`week-cell ${day.day_status === "needs_review" || day.day_status === "missing_clock_out" || day.day_status === "missing_clock_in" ? "attention" : ""}`}>
      <span>
        Kommen <strong>{formatClock(day.first_clock_in)}</strong>
      </span>
      <span>
        Gehen <strong>{formatClock(day.last_clock_out)}</strong>
      </span>
      <span>
        Auftrag <strong>{formatMinutes(day.job_minutes)}</strong>
      </span>
      {day.day_status !== "open" && day.day_status !== "complete" && <StatusPill label={day.day_status === "absence" ? day.absence_label : null} status={day.day_status} />}
    </div>
  );
}

function currentIsoWeek(): { year: number; week: number } {
  const cursor = new Date();
  const utc = new Date(Date.UTC(cursor.getFullYear(), cursor.getMonth(), cursor.getDate()));
  const day = utc.getUTCDay() || 7;
  utc.setUTCDate(utc.getUTCDate() + 4 - day);
  const yearStart = new Date(Date.UTC(utc.getUTCFullYear(), 0, 1));
  return {
    year: utc.getUTCFullYear(),
    week: Math.ceil(((utc.getTime() - yearStart.getTime()) / 86400000 + 1) / 7)
  };
}

function periodToYearMonth(period: string): { year: number; month: number } {
  const now = new Date();
  const [year, month] = period.split("-").map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || month < 1 || month > 12) {
    return { year: now.getFullYear(), month: now.getMonth() + 1 };
  }
  return { year, month };
}

function formatWeekdayHeader(date: string): string {
  const [weekday, day] = formatDate(date).split(",");
  return `${weekday} ${day?.trim() ?? date}`;
}

function EmployeeAdmin({ data, refresh, role }: { data: BootstrapData; refresh: () => Promise<void>; role: Role }) {
  const [drafts, setDrafts] = useState<Record<string, Employee>>({});
  const [newEmployee, setNewEmployee] = useState({ first_name: "", last_name: "", short_code: "", rfid_uid: "" });
  const [pinReset, setPinReset] = useState<{ employee: string; pin: string } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [saveState, setSaveState] = useState<Record<string, { status: "saving" | "saved" | "error"; message: string }>>({});
  const [isCreating, setIsCreating] = useState(false);
  const canManageRoles = role !== "accounting";

  useEffect(() => {
    setDrafts(Object.fromEntries(data.employees.map((employee) => [employee.id, { ...employee }])));
  }, [data.employees]);

  function updateDraft(id: string, patch: Partial<Employee>) {
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  }

  async function save(id: string) {
    const draft = drafts[id];
    if (!draft) return;
    setSaveState((current) => ({ ...current, [id]: { status: "saving", message: "Speichert..." } }));
    try {
      await api<Employee>(`/api/employees/${id}`, {
        method: "PATCH",
        body: JSON.stringify(employeePatch(draft, canManageRoles))
      });
      setSaveState((current) => ({ ...current, [id]: { status: "saved", message: "Gespeichert." } }));
      await refresh();
    } catch (error) {
      setSaveState((current) => ({ ...current, [id]: { status: "error", message: error instanceof Error ? error.message : "Speichern fehlgeschlagen." } }));
    }
  }

  async function createEmployee(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);
    setIsCreating(true);
    try {
      await api<Employee>("/api/employees", { method: "POST", body: JSON.stringify(newEmployee) });
      setNewEmployee({ first_name: "", last_name: "", short_code: "", rfid_uid: "" });
      await refresh();
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Mitarbeiter konnte nicht angelegt werden.");
    } finally {
      setIsCreating(false);
    }
  }

  async function resetPin(id: string) {
    const result = await api<{ employee: Employee; temporary_pin: string }>(`/api/employees/${id}/reset-pin`, {
      method: "POST",
      body: JSON.stringify({ reason: "PIN im Backoffice zurückgesetzt" })
    });
    setPinReset({ employee: result.employee.display_name, pin: result.temporary_pin });
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Mitarbeiterverwaltung</h2>
      <form className="inline-form" onSubmit={(event) => void createEmployee(event)}>
        <input placeholder="Vorname" required value={newEmployee.first_name} onChange={(event) => setNewEmployee({ ...newEmployee, first_name: event.target.value })} />
        <input placeholder="Nachname" required value={newEmployee.last_name} onChange={(event) => setNewEmployee({ ...newEmployee, last_name: event.target.value })} />
        <input placeholder="Kürzel" required value={newEmployee.short_code} onChange={(event) => setNewEmployee({ ...newEmployee, short_code: event.target.value })} />
        <input placeholder="RFID UID" value={newEmployee.rfid_uid} onChange={(event) => setNewEmployee({ ...newEmployee, rfid_uid: event.target.value })} />
        <IconButton disabled={isCreating} icon={<UserPlus />} type="submit" variant="primary">
          {isCreating ? "Speichert" : "Anlegen"}
        </IconButton>
      </form>
      {createError && <p className="form-error">{createError}</p>}
      <div className="employee-grid">
        {data.employees.map((employee) => {
          const draft = drafts[employee.id] ?? employee;
          const vacationYear = new Date(`${data.today}T12:00:00`).getFullYear();
          const vacationUsed = usedVacationDays(data, employee.id, vacationYear);
          const vacationRemaining = Number(draft.annual_vacation_days ?? 0) - vacationUsed;
          return (
            <article className="employee-row" key={employee.id}>
              <label>
                Personalnummer
                <input value={draft.employee_number} onChange={(event) => updateDraft(employee.id, { employee_number: event.target.value })} />
              </label>
              <label>
                Vorname
                <input value={draft.first_name} onChange={(event) => updateDraft(employee.id, { first_name: event.target.value })} />
              </label>
              <label>
                Nachname
                <input value={draft.last_name} onChange={(event) => updateDraft(employee.id, { last_name: event.target.value })} />
              </label>
              <label>
                Kürzel
                <input value={draft.short_code} onChange={(event) => updateDraft(employee.id, { short_code: event.target.value.toUpperCase() })} />
              </label>
              <label>
                RFID
                <input value={draft.rfid_uid ?? ""} onChange={(event) => updateDraft(employee.id, { rfid_uid: event.target.value || null })} />
              </label>
              <label>
                Jahresurlaub Tage
                <input
                  type="number"
                  value={draft.annual_vacation_days ?? 30}
                  onChange={(event) => updateDraft(employee.id, { annual_vacation_days: Number(event.target.value) })}
                />
              </label>
              <div className="employee-mini-metric vacation-balance">
                <span>Resturlaub {vacationYear}</span>
                <strong>{vacationRemaining.toLocaleString("de-DE")} Tage</strong>
                <small>
                  {Number(draft.annual_vacation_days ?? 0).toLocaleString("de-DE")} Anspruch · {vacationUsed.toLocaleString("de-DE")} genommen
                </small>
              </div>
              <div className="employee-mini-metric">
                <span>Pause</span>
                <strong>60 Min.</strong>
              </div>
              <label>
                Mo-Fr Soll
                <input
                  type="number"
                  value={draft.monday_target_minutes}
                  onChange={(event) => {
                    const minutes = Number(event.target.value);
                    updateDraft(employee.id, {
                      monday_target_minutes: minutes,
                      tuesday_target_minutes: minutes,
                      wednesday_target_minutes: minutes,
                      thursday_target_minutes: minutes,
                      friday_target_minutes: minutes
                    });
                  }}
                />
              </label>
              <label>
                Sa Soll
                <input type="number" value={draft.saturday_target_minutes} onChange={(event) => updateDraft(employee.id, { saturday_target_minutes: Number(event.target.value) })} />
              </label>
              <label>
                So Soll
                <input type="number" value={draft.sunday_target_minutes} onChange={(event) => updateDraft(employee.id, { sunday_target_minutes: Number(event.target.value) })} />
              </label>
              <label>
                Aktiv
                <select value={draft.active ? "true" : "false"} onChange={(event) => updateDraft(employee.id, { active: event.target.value === "true" })}>
                  <option value="true">aktiv</option>
                  <option value="false">inaktiv</option>
                </select>
              </label>
              {canManageRoles && (
                <label>
                  Rolle
                  <select value={draft.role} onChange={(event) => updateDraft(employee.id, { role: event.target.value as Role })}>
                    <option value="employee">Mitarbeiter</option>
                    <option value="production_lead">Produktionsleitung</option>
                    <option value="accounting">Buchhaltung</option>
                    <option value="admin">Admin</option>
                    <option value="system_admin">Systemadmin</option>
                  </select>
                </label>
              )}
              <div className="employee-row-actions">
                <IconButton disabled={saveState[employee.id]?.status === "saving"} icon={<BadgeCheck />} onClick={() => void save(employee.id)} variant="primary">
                  {saveState[employee.id]?.status === "saving" ? "Speichert" : "Speichern"}
                </IconButton>
                <IconButton icon={<KeyRound />} onClick={() => void resetPin(employee.id)} variant="ghost">
                  PIN
                </IconButton>
              </div>
              {saveState[employee.id] && (
                <p className={saveState[employee.id].status === "error" ? "form-error" : "form-success"}>
                  {saveState[employee.id].message}
                </p>
              )}
              <p className="employee-row-caption">
                {employee.short_code} · {employee.display_name} · {roleLabels[employee.role]}
              </p>
            </article>
          );
        })}
      </div>
      {pinReset && (
        <p className="form-success">
          Neue PIN für {pinReset.employee}: <strong>{pinReset.pin}</strong>
        </p>
      )}
    </section>
  );
}

function employeePatch(employee: Employee, includeRole: boolean): Partial<Employee> {
  return {
    employee_number: employee.employee_number,
    short_code: employee.short_code,
    first_name: employee.first_name,
    last_name: employee.last_name,
    rfid_uid: employee.rfid_uid,
    active: employee.active,
    monday_target_minutes: employee.monday_target_minutes,
    tuesday_target_minutes: employee.tuesday_target_minutes,
    wednesday_target_minutes: employee.wednesday_target_minutes,
    thursday_target_minutes: employee.thursday_target_minutes,
    friday_target_minutes: employee.friday_target_minutes,
    saturday_target_minutes: employee.saturday_target_minutes,
    sunday_target_minutes: employee.sunday_target_minutes,
    annual_vacation_days: employee.annual_vacation_days,
    ...(includeRole ? { role: employee.role } : {})
  };
}

function usedVacationDays(data: BootstrapData, employeeId: string, year: number): number {
  const vacationTypeIds = new Set(data.absence_types.filter((type) => type.reduces_vacation_balance).map((type) => type.id));
  if (vacationTypeIds.size === 0) return 0;
  const yearStart = `${year}-01-01`;
  const yearEnd = `${year}-12-31`;
  const usedDates = new Set<string>();
  data.absences
    .filter((absence) => absence.employee_id === employeeId && vacationTypeIds.has(absence.absence_type_id) && absence.status !== "cancelled")
    .forEach((absence) => {
      const start = absence.start_date > yearStart ? absence.start_date : yearStart;
      const end = absence.end_date < yearEnd ? absence.end_date : yearEnd;
      if (end < start) return;
      absenceBusinessDates({ ...absence, start_date: start, end_date: end }, data).forEach((date) => usedDates.add(date));
    });
  return usedDates.size;
}

function CorrectionsAdmin({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const [form, setForm] = useState({
    employeeId: data.employees[0]?.id ?? "",
    date: data.today,
    clockIn: "",
    clockOut: "",
    correctionMinutes: 0,
    reason: ""
  });
  const [result, setResult] = useState<AttendanceDay | null>(null);
  const selectedEmployee = data.employees.find((employee) => employee.id === form.employeeId);
  const currentDay = data.attendance_days.find((day) => day.employee_id === form.employeeId && day.date === form.date);

  useEffect(() => {
    if (!currentDay) return;
    setForm((current) => ({
      ...current,
      clockIn: current.clockIn || (currentDay.first_clock_in ? new Date(currentDay.first_clock_in).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : ""),
      clockOut: current.clockOut || (currentDay.last_clock_out ? new Date(currentDay.last_clock_out).toLocaleTimeString("de-DE", { hour: "2-digit", minute: "2-digit" }) : "")
    }));
  }, [currentDay?.id]);

  async function submit(event: FormEvent) {
    event.preventDefault();
    const response = await api<{ day: AttendanceDay }>("/api/attendance/corrections", {
      method: "POST",
      body: JSON.stringify(form)
    });
    setResult(response.day);
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Zeitkorrekturen</h2>
      <form className="inline-form correction-form" onSubmit={(event) => void submit(event)}>
        <select value={form.employeeId} onChange={(event) => setForm({ ...form, employeeId: event.target.value, clockIn: "", clockOut: "" })}>
          {data.employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.short_code} · {employee.display_name}
            </option>
          ))}
        </select>
        <input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value, clockIn: "", clockOut: "" })} />
        <label>
          Arbeitsbeginn
          <input type="time" value={form.clockIn} onChange={(event) => setForm({ ...form, clockIn: event.target.value })} />
        </label>
        <label>
          Arbeitsende
          <input type="time" value={form.clockOut} onChange={(event) => setForm({ ...form, clockOut: event.target.value })} />
        </label>
        <label>
          Tageskorrektur Min.
          <input type="number" value={form.correctionMinutes} onChange={(event) => setForm({ ...form, correctionMinutes: Number(event.target.value) })} />
        </label>
        <input placeholder="Korrekturgrund" value={form.reason} onChange={(event) => setForm({ ...form, reason: event.target.value })} />
        <IconButton icon={<BadgeCheck />} type="submit" variant="primary">
          Korrektur speichern
        </IconButton>
      </form>
      {currentDay && (
        <div className="metrics-grid">
          <Metric label="Aktuell Kommen" value={formatClock(currentDay.first_clock_in)} />
          <Metric label="Aktuell Gehen" value={formatClock(currentDay.last_clock_out)} />
          <Metric label="Netto" value={formatMinutes(currentDay.net_work_minutes)} />
          <Metric label="Saldo" value={formatMinutes(currentDay.balance_minutes, true)} tone={currentDay.balance_minutes >= 0 ? "good" : "warn"} />
        </div>
      )}
      {result && selectedEmployee && (
        <p className="form-success">
          Korrektur gespeichert für {selectedEmployee.short_code}: Netto {formatMinutes(result.net_work_minutes)}, Saldo {formatMinutes(result.balance_minutes, true)}.
        </p>
      )}
    </section>
  );
}

function AbsenceAdmin({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const defaultEmployeeId = data.employees[0]?.id ?? "";
  const [form, setForm] = useState({
    employee_id: defaultEmployeeId,
    absence_type_id: data.absence_types[0]?.id ?? "",
    start_date: data.today,
    end_date: data.today,
    status: "approved" as AbsenceStatus,
    comment: ""
  });
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(defaultEmployeeId);
  const [drafts, setDrafts] = useState<Record<string, Absence>>({});
  const [message, setMessage] = useState<{ tone: "success" | "error"; text: string } | null>(null);

  useEffect(() => {
    setDrafts(Object.fromEntries(data.absences.map((absence) => [absence.id, { ...absence }])));
  }, [data.absences]);

  useEffect(() => {
    if (!selectedEmployeeId && defaultEmployeeId) setSelectedEmployeeId(defaultEmployeeId);
    if (!form.employee_id && defaultEmployeeId) setForm((current) => ({ ...current, employee_id: defaultEmployeeId }));
  }, [defaultEmployeeId, form.employee_id, selectedEmployeeId]);

  const selectedEmployee = data.employees.find((employee) => employee.id === selectedEmployeeId) ?? data.employees[0] ?? null;
  const currentYear = new Date(`${data.today}T12:00:00`).getFullYear();
  const selectedAbsences = data.absences
    .filter((absence) => absence.employee_id === selectedEmployee?.id)
    .sort((a, b) => b.start_date.localeCompare(a.start_date) || b.created_at.localeCompare(a.created_at));
  const vacationTypeIds = new Set(data.absence_types.filter((type) => type.reduces_vacation_balance).map((type) => type.id));
  const vacationDays = selectedAbsences
    .filter((absence) => absence.status !== "cancelled" && vacationTypeIds.has(absence.absence_type_id))
    .flatMap((absence) => absenceBusinessDates(absence, data))
    .filter((date, index, dates) => dates.indexOf(date) === index)
    .sort((a, b) => a.localeCompare(b));
  const vacationUsed = selectedEmployee ? usedVacationDays(data, selectedEmployee.id, currentYear) : 0;
  const vacationAnnual = Number(selectedEmployee?.annual_vacation_days ?? 0);
  const vacationRemaining = vacationAnnual - vacationUsed;

  function updateDraft(id: string, patch: Partial<Absence>) {
    setDrafts((current) => ({ ...current, [id]: { ...current[id], ...patch } }));
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    setMessage(null);
    await api("/api/absences", { method: "POST", body: JSON.stringify({ ...form, is_full_day: true }) });
    setSelectedEmployeeId(form.employee_id);
    setForm((current) => ({ ...current, start_date: data.today, end_date: data.today, comment: "" }));
    setMessage({ tone: "success", text: "Fehlzeit erfasst." });
    await refresh();
  }

  async function saveAbsence(id: string) {
    const draft = drafts[id];
    if (!draft) return;
    setMessage(null);
    try {
      await api<Absence>(`/api/absences/${id}`, { method: "PATCH", body: JSON.stringify({ ...draft, is_full_day: true }) });
      setMessage({ tone: "success", text: "Fehlzeit korrigiert." });
      await refresh();
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Fehlzeit konnte nicht gespeichert werden." });
    }
  }

  async function cancelAbsence(absence: Absence) {
    const draft = drafts[absence.id] ?? absence;
    setMessage(null);
    try {
      await api<Absence>(`/api/absences/${absence.id}`, { method: "PATCH", body: JSON.stringify({ ...draft, status: "cancelled" }) });
      setMessage({ tone: "success", text: "Fehlzeit storniert." });
      await refresh();
    } catch (error) {
      setMessage({ tone: "error", text: error instanceof Error ? error.message : "Fehlzeit konnte nicht storniert werden." });
    }
  }

  return (
    <section className="wide-panel">
      <h2>Urlaub, Krankheit und Fehlzeiten</h2>
      <form className="inline-form" onSubmit={(event) => void submit(event)}>
        <select value={form.employee_id} onChange={(event) => setForm({ ...form, employee_id: event.target.value })}>
          {data.employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.short_code} · {employee.display_name}
            </option>
          ))}
        </select>
        <select value={form.absence_type_id} onChange={(event) => setForm({ ...form, absence_type_id: event.target.value })}>
          {data.absence_types.map((type) => (
            <option key={type.id} value={type.id}>
              {type.name}
            </option>
          ))}
        </select>
        <input type="date" value={form.start_date} onChange={(event) => setForm({ ...form, start_date: event.target.value })} />
        <input type="date" value={form.end_date} onChange={(event) => setForm({ ...form, end_date: event.target.value })} />
        <select value={form.status} onChange={(event) => setForm({ ...form, status: event.target.value as AbsenceStatus })}>
          {Object.entries(absenceStatusLabels).map(([status, label]) => (
            <option key={status} value={status}>
              {label}
            </option>
          ))}
        </select>
        <input placeholder="Kommentar" value={form.comment} onChange={(event) => setForm({ ...form, comment: event.target.value })} />
        <IconButton icon={<CalendarDays />} type="submit" variant="primary">
          Erfassen
        </IconButton>
      </form>

      {message && <p className={message.tone === "error" ? "form-error" : "form-success"}>{message.text}</p>}

      <div className="absence-toolbar">
        <label>
          Mitarbeiter anzeigen
          <select value={selectedEmployee?.id ?? ""} onChange={(event) => setSelectedEmployeeId(event.target.value)}>
            {data.employees.map((employee) => (
              <option key={employee.id} value={employee.id}>
                {employee.short_code} · {employee.display_name}
              </option>
            ))}
          </select>
        </label>
      </div>

      {selectedEmployee && (
        <div className="absence-overview">
          <div>
            <span>Resturlaub {currentYear}</span>
            <strong>{vacationRemaining.toLocaleString("de-DE")} Tage</strong>
            <small>
              {vacationAnnual.toLocaleString("de-DE")} Anspruch · {vacationUsed.toLocaleString("de-DE")} genommen
            </small>
          </div>
          <div className="absence-day-list" aria-label="Eingetragene Urlaubstage">
            {vacationDays.length === 0 ? (
              <span className="absence-day-empty">Keine Urlaubstage eingetragen</span>
            ) : (
              vacationDays.map((date) => (
                <span className="absence-day-chip" key={date}>
                  {formatDate(date)}
                </span>
              ))
            )}
          </div>
        </div>
      )}

      <div className="table-wrap absence-table-wrap">
        <table className="absence-table">
          <thead>
            <tr>
              <th>Mitarbeiter</th>
              <th>Art</th>
              <th>Von</th>
              <th>Bis</th>
              <th>Tage</th>
              <th>Status</th>
              <th>Kommentar</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {selectedAbsences.map((absence) => {
              const draft = drafts[absence.id] ?? absence;
              return (
                <tr className={draft.status === "cancelled" ? "muted-row" : ""} key={absence.id}>
                  <td>{data.employees.find((employee) => employee.id === draft.employee_id)?.short_code}</td>
                  <td>
                    <select value={draft.absence_type_id} onChange={(event) => updateDraft(absence.id, { absence_type_id: event.target.value })}>
                      {data.absence_types.map((type) => (
                        <option key={type.id} value={type.id}>
                          {type.name}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input type="date" value={draft.start_date} onChange={(event) => updateDraft(absence.id, { start_date: event.target.value })} />
                  </td>
                  <td>
                    <input type="date" value={draft.end_date} onChange={(event) => updateDraft(absence.id, { end_date: event.target.value })} />
                  </td>
                  <td>{absenceBusinessDayCount(draft, data).toLocaleString("de-DE")}</td>
                  <td>
                    <select value={draft.status} onChange={(event) => updateDraft(absence.id, { status: event.target.value as AbsenceStatus })}>
                      {Object.entries(absenceStatusLabels).map(([status, label]) => (
                        <option key={status} value={status}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td>
                    <input value={draft.comment ?? ""} onChange={(event) => updateDraft(absence.id, { comment: event.target.value || null })} />
                  </td>
                  <td>
                    <div className="row-actions">
                      <button className="small-action" onClick={() => void saveAbsence(absence.id)}>
                        Speichern
                      </button>
                      {draft.status !== "cancelled" && (
                        <button className="small-action danger" onClick={() => void cancelAbsence(absence)}>
                          <CalendarX size={15} /> Stornieren
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
            {selectedAbsences.length === 0 && (
              <tr>
                <td colSpan={8}>Für diesen Mitarbeiter sind keine Fehlzeiten eingetragen.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function absenceBusinessDates(absence: Absence, data: BootstrapData): string[] {
  const dates: string[] = [];
  const cursor = new Date(`${absence.start_date}T12:00:00`);
  const end = new Date(`${absence.end_date}T12:00:00`);
  if (Number.isNaN(cursor.getTime()) || Number.isNaN(end.getTime()) || cursor > end) return dates;
  while (cursor <= end) {
    const date = todayKey(cursor);
    if (isBusinessWeekday(date) && !holidayForDate(data.holidays, date)) dates.push(date);
    cursor.setDate(cursor.getDate() + 1);
  }
  return dates;
}

function absenceBusinessDayCount(absence: Absence, data: BootstrapData): number {
  return businessDaysExcludingHolidaysInRange(absence.start_date, absence.end_date, data.holidays);
}

function HolidayAdmin({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const [form, setForm] = useState({ date: data.today, name: "", federal_state: "NRW", affects_working_time: true, active: true });

  async function submit(event: FormEvent) {
    event.preventDefault();
    await api("/api/holidays", { method: "POST", body: JSON.stringify(form) });
    setForm({ date: data.today, name: "", federal_state: "NRW", affects_working_time: true, active: true });
    await refresh();
  }

  async function toggle(id: string, active: boolean) {
    await api(`/api/holidays/${id}`, { method: "PATCH", body: JSON.stringify({ active }) });
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Feiertage NRW</h2>
      <form className="inline-form" onSubmit={(event) => void submit(event)}>
        <input type="date" value={form.date} onChange={(event) => setForm({ ...form, date: event.target.value })} />
        <input placeholder="Name" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        <input placeholder="Bundesland" value={form.federal_state} onChange={(event) => setForm({ ...form, federal_state: event.target.value })} />
        <label className="checkbox-label">
          <input checked={form.affects_working_time} onChange={(event) => setForm({ ...form, affects_working_time: event.target.checked })} type="checkbox" />
          Arbeitszeitrelevant
        </label>
        <IconButton icon={<CalendarDays />} type="submit" variant="primary">
          Feiertag anlegen
        </IconButton>
      </form>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Name</th>
              <th>Bundesland</th>
              <th>Arbeitszeit</th>
              <th>Status</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {data.holidays.map((holiday) => (
              <tr key={holiday.id}>
                <td>{holiday.date}</td>
                <td>{holiday.name}</td>
                <td>{holiday.federal_state}</td>
                <td>{holiday.affects_working_time ? "ja" : "nein"}</td>
                <td>{holiday.active ? "aktiv" : "inaktiv"}</td>
                <td>
                  <button className="small-action" onClick={() => void toggle(holiday.id, !holiday.active)}>
                    {holiday.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ManagedPasswordsAdmin({ refresh }: { refresh: () => Promise<void> }) {
  const [targets, setTargets] = useState<ManagedAccessTarget[]>([]);
  const [forms, setForms] = useState<Record<string, { password: string; repeat: string }>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadTargets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await api<{ targets: ManagedAccessTarget[] }>("/api/users/managed-access-targets");
      setTargets(result.targets);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passwort-Zugänge konnten nicht geladen werden.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadTargets();
  }, [loadTargets]);

  function updateForm(key: string, patch: Partial<{ password: string; repeat: string }>) {
    setForms((current) => ({ ...current, [key]: { ...(current[key] ?? { password: "", repeat: "" }), ...patch } }));
  }

  function suggestPassword(target: ManagedAccessTarget) {
    const password = generateManagedPassword(target.label);
    setForms((current) => ({ ...current, [target.key]: { password, repeat: password } }));
    setMessage(`Passwortvorschlag für ${target.label} wurde eingetragen.`);
    setError(null);
  }

  async function setPassword(target: ManagedAccessTarget) {
    const form = forms[target.key] ?? { password: "", repeat: "" };
    if (form.password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein.");
      return;
    }
    if (form.password !== form.repeat) {
      setError("Die beiden Passwörter stimmen nicht überein.");
      return;
    }
    setSaving(target.key);
    setError(null);
    setMessage(null);
    try {
      const result = await api<{ target: ManagedAccessTarget }>(`/api/users/managed-access-targets/${target.key}/password`, {
        method: "POST",
        body: JSON.stringify({ password: form.password })
      });
      setTargets((current) => current.map((item) => (item.key === target.key ? result.target : item)));
      setForms((current) => ({ ...current, [target.key]: { password: "", repeat: "" } }));
      setMessage(`Passwort für ${target.label} wurde gesetzt: ${form.password}`);
      await refresh();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Passwort konnte nicht gesetzt werden.");
    } finally {
      setSaving(null);
    }
  }

  return (
    <section className="wide-panel">
      <div className="panel-heading-row">
        <div>
          <h2>Passwörter für feste Zugänge</h2>
          <p>Hier werden die drei extern nutzbaren Zeiterfassungs-Zugänge gezielt angelegt oder mit einem neuen Passwort versehen.</p>
        </div>
        <IconButton disabled={loading} icon={<RefreshCw />} onClick={() => void loadTargets()} variant="secondary">
          Aktualisieren
        </IconButton>
      </div>

      {loading && <p className="hint">Passwort-Zugänge werden geladen...</p>}
      {error && <p className="form-error">{error}</p>}
      {message && <p className="form-success">{message}</p>}

      <div className="managed-password-grid">
        {targets.map((target) => {
          const form = forms[target.key] ?? { password: "", repeat: "" };
          const statusLabel = target.user ? (target.user.active ? "aktiv" : "inaktiv") : "wird angelegt";
          return (
            <article className="managed-password-card" key={target.key}>
              <header>
                <div>
                  <h3>{target.label}</h3>
                  <p>{target.username}</p>
                </div>
                <span className={`managed-status ${target.user?.active ? "active" : "pending"}`}>{statusLabel}</span>
              </header>
              <dl>
                <div>
                  <dt>Rolle</dt>
                  <dd>{roleLabels[target.role]}</dd>
                </div>
                <div>
                  <dt>E-Mail</dt>
                  <dd>{target.email}</dd>
                </div>
                <div>
                  <dt>Mitarbeiterlink</dt>
                  <dd>{target.employee_number ? `Personalnummer ${target.employee_number}` : "kein Mitarbeiterlink"}</dd>
                </div>
                <div>
                  <dt>Letzte Änderung</dt>
                  <dd>{target.user?.updated_at ? new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(new Date(target.user.updated_at)) : "-"}</dd>
                </div>
              </dl>
              {target.employee_missing && <p className="form-error">Personalnummer {target.employee_number} wurde noch nicht gefunden. Dieser Zugang kann erst danach gesetzt werden.</p>}
              <label>
                Neues Passwort
                <input autoComplete="new-password" type="password" value={form.password} onChange={(event) => updateForm(target.key, { password: event.target.value })} />
              </label>
              <label>
                Passwort wiederholen
                <input autoComplete="new-password" type="password" value={form.repeat} onChange={(event) => updateForm(target.key, { repeat: event.target.value })} />
              </label>
              <div className="row-actions">
                <IconButton icon={<RefreshCw />} onClick={() => suggestPassword(target)} variant="secondary">
                  Vorschlag
                </IconButton>
                <IconButton disabled={saving === target.key || target.employee_missing} icon={<KeyRound />} onClick={() => void setPassword(target)} variant="primary">
                  {saving === target.key ? "Speichert" : "Passwort setzen"}
                </IconButton>
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}

function generateManagedPassword(label: string): string {
  const randomValues = new Uint32Array(2);
  if (window.crypto?.getRandomValues) {
    window.crypto.getRandomValues(randomValues);
  } else {
    randomValues[0] = Math.floor(Math.random() * 100000);
    randomValues[1] = Math.floor(Math.random() * 100000);
  }
  const prefix =
    label
      .normalize("NFD")
      .replace(/[^A-Za-z0-9]/g, "")
      .slice(0, 3)
      .toUpperCase() || "DZR";
  return `DZR-${prefix}-${100 + (randomValues[0] % 900)}-${100 + (randomValues[1] % 900)}!`;
}

function UsersAdmin({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const [form, setForm] = useState({ username: "", email: "", role: "accounting" as Role, employee_id: "", password: "demo" });
  const [passwordReset, setPasswordReset] = useState<{ username: string; password: string } | null>(null);
  const [createError, setCreateError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setCreateError(null);
    setIsCreating(true);
    try {
      await api("/api/users", {
        method: "POST",
        body: JSON.stringify({ ...form, employee_id: form.employee_id || null })
      });
      setForm({ username: "", email: "", role: "accounting", employee_id: "", password: "demo" });
      await refresh();
    } catch (error) {
      setCreateError(error instanceof Error ? error.message : "Benutzer konnte nicht angelegt werden.");
    } finally {
      setIsCreating(false);
    }
  }

  async function updateUser(id: string, patch: Record<string, unknown>) {
    await api(`/api/users/${id}`, { method: "PATCH", body: JSON.stringify(patch) });
    await refresh();
  }

  async function resetPassword(user: { id: string; username: string }) {
    const result = await api<{ temporary_password: string }>(`/api/users/${user.id}/reset-password`, {
      method: "POST",
      body: JSON.stringify({ password: "demo", reason: "Passwort im Adminbereich zurückgesetzt" })
    });
    setPasswordReset({ username: user.username, password: result.temporary_password });
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Benutzer und Rollen</h2>
      <form className="inline-form" onSubmit={(event) => void submit(event)}>
        <input placeholder="Benutzername" required value={form.username} onChange={(event) => setForm({ ...form, username: event.target.value })} />
        <input placeholder="E-Mail" type="email" value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} />
        <select value={form.role} onChange={(event) => setForm({ ...form, role: event.target.value as Role })}>
          <option value="employee">Mitarbeiter</option>
          <option value="production_lead">Produktionsleitung</option>
          <option value="accounting">Buchhaltung</option>
          <option value="admin">Admin</option>
          <option value="system_admin">Systemadmin</option>
        </select>
        <select value={form.employee_id} onChange={(event) => setForm({ ...form, employee_id: event.target.value })}>
          <option value="">Kein Mitarbeiterlink</option>
          {data.employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.short_code} · {employee.display_name}
            </option>
          ))}
        </select>
        <input placeholder="Initialpasswort" required value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} />
        <IconButton disabled={isCreating} icon={<UserPlus />} type="submit" variant="primary">
          {isCreating ? "Speichert" : "Benutzer anlegen"}
        </IconButton>
      </form>
      {createError && <p className="form-error">{createError}</p>}
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Benutzer</th>
              <th>E-Mail</th>
              <th>Rolle</th>
              <th>Mitarbeiter</th>
              <th>Status</th>
              <th>Aktionen</th>
            </tr>
          </thead>
          <tbody>
            {data.users.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>
                  <input defaultValue={user.email ?? ""} onBlur={(event) => void updateUser(user.id, { email: event.target.value })} />
                </td>
                <td>
                  <select value={user.role} onChange={(event) => void updateUser(user.id, { role: event.target.value })}>
                    <option value="employee">Mitarbeiter</option>
                    <option value="production_lead">Produktionsleitung</option>
                    <option value="accounting">Buchhaltung</option>
                    <option value="admin">Admin</option>
                    <option value="system_admin">Systemadmin</option>
                  </select>
                </td>
                <td>{data.employees.find((employee) => employee.id === user.employee_id)?.short_code ?? "-"}</td>
                <td>{user.active ? "aktiv" : "inaktiv"}</td>
                <td className="row-actions">
                  <button className="small-action" onClick={() => void updateUser(user.id, { active: !user.active })}>
                    {user.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                  <button className="small-action" onClick={() => void resetPassword(user)}>
                    Passwort
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {passwordReset && (
        <p className="form-success">
          Neues Passwort für {passwordReset.username}: <strong>{passwordReset.password}</strong>
        </p>
      )}
    </section>
  );
}

function TerminalAdmin({ data, refresh }: { data: BootstrapData; refresh: () => Promise<void> }) {
  const [form, setForm] = useState({ name: "", location: "Produktion" });

  async function submit(event: FormEvent) {
    event.preventDefault();
    await api("/api/terminals", { method: "POST", body: JSON.stringify({ ...form, active: true }) });
    setForm({ name: "", location: "Produktion" });
    await refresh();
  }

  async function toggle(id: string, active: boolean) {
    await api(`/api/terminals/${id}`, { method: "PATCH", body: JSON.stringify({ active }) });
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Terminalverwaltung</h2>
      <form className="inline-form" onSubmit={(event) => void submit(event)}>
        <input placeholder="Terminalname" value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        <input placeholder="Standort" value={form.location} onChange={(event) => setForm({ ...form, location: event.target.value })} />
        <IconButton icon={<MonitorCheck />} type="submit" variant="primary">
          Terminal anlegen
        </IconButton>
      </form>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Name</th>
              <th>Standort</th>
              <th>Status</th>
              <th>Letztes Signal</th>
              <th>Aktion</th>
            </tr>
          </thead>
          <tbody>
            {data.terminals.map((terminal) => (
              <tr key={terminal.id}>
                <td>{terminal.name}</td>
                <td>{terminal.location}</td>
                <td>{terminal.active ? "aktiv" : "inaktiv"}</td>
                <td>{terminal.last_seen_at ? new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(new Date(terminal.last_seen_at)) : "-"}</td>
                <td>
                  <button className="small-action" onClick={() => void toggle(terminal.id, !terminal.active)}>
                    {terminal.active ? "Deaktivieren" : "Aktivieren"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function ClosureAdmin({ data, refresh, canLock }: { data: BootstrapData; refresh: () => Promise<void>; canLock: boolean }) {
  const now = new Date();
  const [employeeId, setEmployeeId] = useState(data.employees[0]?.id ?? "");
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const [closure, setClosure] = useState<MonthlyClosure | null>(null);

  async function calculate() {
    const { year, month } = periodToYearMonth(period);
    const result = await api<MonthlyClosure>("/api/monthly-closures/calculate", {
      method: "POST",
      body: JSON.stringify({ employeeId, year, month })
    });
    setClosure(result);
    await refresh();
  }

  async function setStatus(status: MonthlyClosure["status"]) {
    if (!closure) return;
    const result = await api<MonthlyClosure>(`/api/monthly-closures/${closure.id}/status`, {
      method: "POST",
      body: JSON.stringify({ status })
    });
    setClosure(result);
    await refresh();
  }

  return (
    <section className="wide-panel">
      <h2>Monatsabschluss</h2>
      <div className="inline-form">
        <select value={employeeId} onChange={(event) => setEmployeeId(event.target.value)}>
          {data.employees.map((employee) => (
            <option key={employee.id} value={employee.id}>
              {employee.short_code} · {employee.display_name}
            </option>
          ))}
        </select>
        <input aria-label="Monat auswählen" type="month" value={period} onChange={(event) => setPeriod(event.target.value)} />
        <IconButton icon={<Calculator />} onClick={() => void calculate()} variant="primary">
          Monat berechnen
        </IconButton>
      </div>
      {closure && (
        <>
          <ClosureSummary closure={closure} employee={data.employees.find((employee) => employee.id === closure.employee_id)!} />
          <div className="terminal-actions compact-actions">
            <IconButton icon={<BadgeCheck />} onClick={() => void setStatus("approved")} variant="secondary">
              Freigeben
            </IconButton>
            {canLock && (
              <>
                <IconButton icon={<ShieldCheck />} onClick={() => void setStatus("locked")} variant="secondary">
                  Sperren
                </IconButton>
                <IconButton icon={<RefreshCw />} onClick={() => void setStatus("reopened")} variant="secondary">
                  Wieder öffnen
                </IconButton>
              </>
            )}
          </div>
        </>
      )}
    </section>
  );
}

function ClosureSummary({ closure, employee }: { closure: MonthlyClosure; employee: Employee }) {
  return (
    <section className="summary-band">
      <div>
        <p>
          {employee.short_code} · {employee.display_name}
        </p>
        <h2>
          Monatsabschluss {String(closure.month).padStart(2, "0")}.{closure.year}
        </h2>
      </div>
      <StatusPill status={closure.status} />
      <div className="metrics-grid">
        <Metric label="Sollzeit" value={formatMinutes(closure.target_minutes)} />
        <Metric label="Istzeit" value={formatMinutes(closure.worked_minutes)} />
        <Metric label="Urlaub" value={formatMinutes(closure.vacation_minutes)} />
        <Metric label="Krankheit" value={formatMinutes(closure.sick_minutes)} />
        <Metric label="Feiertage" value={formatMinutes(closure.holiday_minutes)} />
        <Metric label="Berufsschule / sonstige Freistellung" value={formatMinutes(closure.other_absence_minutes)} />
        <Metric label="Monatssaldo" value={formatMinutes(closure.month_balance_minutes, true)} tone={closure.month_balance_minutes >= 0 ? "good" : "warn"} />
        <Metric label="Vortrag" value={formatMinutes(closure.carryover_previous_minutes, true)} />
        <Metric label="Neuer Kontostand" value={formatMinutes(closure.carryover_new_minutes, true)} tone={closure.carryover_new_minutes >= 0 ? "good" : "warn"} />
      </div>
    </section>
  );
}

function ExportPanel() {
  const now = new Date();
  const [period, setPeriod] = useState(`${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`);
  const { year, month } = periodToYearMonth(period);
  const query = `year=${year}&month=${month}`;

  return (
    <section className="wide-panel export-panel">
      <h2>CSV- und Excel-Exporte</h2>
      <p>Arbeitszeiten und Monatsabschlüsse können für Buchhaltung und Lohnvorbereitung exportiert werden.</p>
      <label className="export-period">
        Exportmonat
        <input type="month" value={period} onChange={(event) => setPeriod(event.target.value)} />
      </label>
      <div className="export-links">
        <a href={`/api/exports/attendance.csv?${query}`}>
          <Download /> Arbeitszeiten CSV
        </a>
        <a href={`/api/exports/attendance.xlsx?${query}`}>
          <FileSpreadsheet /> Arbeitszeiten Excel
        </a>
        <a href={`/api/exports/monthly.csv?${query}`}>
          <Download /> Monatsabschluss CSV
        </a>
        <a href={`/api/exports/monthly.xlsx?${query}`}>
          <FileSpreadsheet /> Monatsabschluss Excel
        </a>
      </div>
    </section>
  );
}

function AuditPanel({ data }: { data: BootstrapData }) {
  return (
    <section className="wide-panel">
      <h2>Audit-Log</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Zeitpunkt</th>
              <th>Aktion</th>
              <th>Objekt</th>
              <th>Grund</th>
            </tr>
          </thead>
          <tbody>
            {data.audit_log.slice(0, 50).map((entry) => (
              <tr key={entry.id}>
                <td>{new Intl.DateTimeFormat("de-DE", { dateStyle: "short", timeStyle: "short" }).format(new Date(entry.timestamp))}</td>
                <td>{entry.action_type}</td>
                <td>{entry.entity_type}</td>
                <td>{entry.reason}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function SystemPanel() {
  const [status, setStatus] = useState<Record<string, unknown> | null>(null);

  useEffect(() => {
    void api<Record<string, unknown>>("/api/system/status").then(setStatus);
  }, []);

  return (
    <section className="wide-panel">
      <h2>Systemstatus</h2>
      <div className="system-grid">
        <Metric label="Service" value={String(status?.service ?? "lädt")} tone="good" />
        <Metric label="Datenbank" value={String(status?.database ?? "-")} />
        <Metric label="Healthcheck" value="/health" />
        <Metric label="Backup" value={String((status?.backup as { status?: string } | undefined)?.status ?? "prepared")} />
      </div>
      <pre className="system-json">{JSON.stringify(status, null, 2)}</pre>
    </section>
  );
}

function DayTable({ days, title }: { days: AttendanceDay[]; title: string }) {
  return (
    <section className="wide-panel">
      <h2>{title}</h2>
      <div className="table-wrap">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Status</th>
              <th>Kommen</th>
              <th>Gehen</th>
              <th>Netto</th>
              <th>Soll</th>
              <th>Saldo</th>
            </tr>
          </thead>
          <tbody>
            {days.map((day) => (
              <tr key={day.id}>
                <td>{formatDate(day.date)}</td>
                <td>
                  <StatusPill label={day.day_status === "absence" ? day.absence_label : null} status={day.day_status} />
                </td>
                <td>{formatClock(day.first_clock_in)}</td>
                <td>{formatClock(day.last_clock_out)}</td>
                <td>{formatMinutes(day.net_work_minutes)}</td>
                <td>{formatMinutes(day.target_minutes)}</td>
                <td className={day.balance_minutes >= 0 ? "positive" : "negative"}>{formatMinutes(day.balance_minutes, true)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function EmployeeHelp() {
  return (
    <section className="wide-panel help-copy">
      <h2>Hilfe im Mitarbeiterportal</h2>
      <p>Im Mitarbeiterportal kannst du deine eigenen Zeiten einsehen: Tageszeiten, Wochenübersicht, Monatsübersicht, Urlaub, Krankheit, Feiertage und den aktuellen Stand deines Arbeitszeitkontos.</p>
      <p>Du kannst deine Zeiten nicht selbst ändern. Wenn etwas fehlt oder falsch ist, melde dich bitte bei Schichtleitung oder Buchhaltung.</p>
    </section>
  );
}

function HelpManual({ onBack }: { onBack: () => void }) {
  const chapters = useMemo(
    () => [
      {
        title: "Anmeldung am Terminal",
        body: "Mitarbeiter melden sich mit ihrer RFID-Karte an. Bei Kartenproblemen steht auf dem Startbildschirm Ohne Karte anmelden für das bekannte Namenskürzel und die eigene PIN bereit.",
        bullets: [
          "Karte kurz an den Leser halten.",
          "Nach erfolgreicher Erkennung erscheint die persönliche Buchungsseite.",
          "Die erste eigene PIN wird nach dem Vorhalten der Karte über Eigene PIN einrichten vergeben: 4 bis 8 Ziffern zweimal eingeben.",
          "PIN-Einrichtung und Anmeldung buchen noch keine Arbeitszeit. Danach Arbeitsbeginn oder Arbeitsende wie gewohnt buchen.",
          "PIN ändern: Karte vorhalten, Eigene PIN ändern wählen und bisherige PIN eingeben. Bei vergessener PIN hilft die Buchhaltung."
        ]
      },
      {
        title: "Arbeitsbeginn und Arbeitsende",
        body: "Die arbeitsrechtliche Zeit wird über Kommen, Gehen und Unterbrechungen geführt. Diese Zeiten sind die Basis für Nettoarbeitszeit, Pausen, Tagesstatus, Arbeitszeitkonto und Monatsabschluss.",
        bullets: [
          "Bei Arbeitsantritt einbuchen.",
          "Nach Arbeitsende direkt ausbuchen.",
          "Beim Ausstempeln wird eine Zusammenfassung mit Eingestempelt um, Ausgestempelt um, Nettoarbeitszeit, gebuchter Auftragszeit und Differenz angezeigt."
        ]
      },
      {
        title: "Unterbrechung",
        body: "Eine Unterbrechung ist für echte Pausen oder Arbeitsunterbrechungen am gleichen Arbeitstag gedacht. Der Arbeitstag bleibt offen und kann später fortgesetzt werden.",
        bullets: [
          "Während einer Unterbrechung können keine Auftragszeiten gebucht werden.",
          "Nach Arbeit wieder aufnehmen kann wieder auf Jobs gebucht werden.",
          "Auftragszeiten dürfen nicht in Unterbrechungszeiträume fallen."
        ]
      },
      {
        title: "Auftragszeit buchen",
        body: "Auftragszeiten sind produktionsbezogene Zeiten. Sie werden getrennt von der Mitarbeiterzeit gespeichert und verändern Arbeitszeitkonto, Lohnzeit, Urlaub, Krankheit und Monatsabschluss nicht automatisch.",
        bullets: [
          "Auftragszeit ist nur möglich, wenn der Mitarbeiter eingestempelt und nicht in Unterbrechung ist.",
          "Es kann pro Mitarbeiter immer nur eine Auftragszeit gleichzeitig laufen.",
          "Laufende Auftragszeit kann gestoppt oder durch Auswahl eines anderen Jobs gewechselt werden."
        ]
      },
      {
        title: "Jobauswahl am Touchmonitor",
        body: "Die Jobauswahl ist für den 21,5/22-Zoll-Touchmonitor ausgelegt. Der große Button öffnet ein Vollbildfenster, damit Jobs ohne präzises Tippen ausgewählt werden können.",
        bullets: [
          "Die Suche startet nicht automatisch; die Bildschirmtastatur öffnet erst beim Tippen ins Suchfeld.",
          "Aufträge werden aus dem Auftragstool gelesen und nur angezeigt, wenn sie aktiv und im PAL-Zeitfenster liegen.",
          "Abgerechnete Aufträge werden nicht zur Buchung angeboten."
        ]
      },
      {
        title: "Tätigkeit auswählen",
        body: "Nach der Jobauswahl werden nur Tätigkeiten angeboten, die beim Auftrag im Bereich Produktion hinterlegt sind. Die Kacheln helfen, die passende Tätigkeit schnell zu erkennen.",
        bullets: [
          "Wenn keine Tätigkeit aus dem Auftrag gelesen werden kann, wird Sonstiges / nicht zugeordnet angeboten.",
          "Die Tätigkeit dient der Aufschlüsselung und erhöht die Gesamtzeit nicht zusätzlich.",
          "Falsche Tätigkeit bitte über Produktionsleitung korrigieren lassen."
        ]
      },
      {
        title: "Meine Auftragszeiten heute",
        body: "Im Terminal sehen Mitarbeiter ihre heute gebuchten Auftragszeiten. Das hilft beim Prüfen, ob die produktive Zeit vollständig und auf dem richtigen Auftrag gelandet ist.",
        bullets: [
          "Angezeigt werden Job, Tätigkeit, Start, Ende und Minuten.",
          "Offene Buchungen sind als laufend erkennbar.",
          "Korrekturen erfolgen nicht am Terminal, sondern im Backoffice."
        ]
      },
      {
        title: "Produktion / Auftragszeiten",
        body: "Der Bereich Produktion ist für Produktionsleitung und berechtigte Personen. Dort werden aktive Aufträge, heutige Auftragszeiten, Auftragsdetails, Mitarbeiterzeiten, Korrekturen, Sync-Status, Differenzreport und Export verwaltet.",
        bullets: [
          "Manuelle Plus- und Minusminuten werden als eigene Einträge mit Grund gespeichert.",
          "Livezeiten werden durch Korrekturen nicht überschrieben.",
          "Alle Korrekturen und Sync-Vorgänge werden auditierbar dokumentiert."
        ]
      },
      {
        title: "Differenzreport",
        body: "Der Differenzreport vergleicht Nettoarbeitszeit aus der Zeiterfassung mit gebuchter Auftragszeit aus der Produktion.",
        bullets: [
          "Auftragszeit größer als Nettoarbeitszeit wird als Warnung markiert.",
          "Auftragszeit kleiner als Nettoarbeitszeit zeigt nicht zugeordnete Zeit.",
          "Der Report ist ein Prüfwerkzeug und ändert keine Mitarbeiterzeiten."
        ]
      },
      {
        title: "Auftragstool-Sync",
        body: "Abgeschlossene Auftragszeiten werden additiv in das Minutenfeld des bestehenden Auftragstools übertragen. Das System überschreibt keine vorhandenen Minuten blind.",
        bullets: [
          "Der automatische Tages-Sync läuft für abgeschlossene Zeiten des Vortags.",
          "Nachträgliche Plus-/Minuskorrekturen werden beim nächsten Sync als Differenz berücksichtigt.",
          "Doppeladdition wird über den Sync-Status technisch verhindert."
        ]
      },
      {
        title: "Export",
        body: "Im Exportbereich kann eine Exceldatei mit Auftragszeiten erzeugt werden. Je Auftrag gibt es eine Zeile mit Gesamtminuten, Gesamtstunden, Mitarbeiteraufschlüsselung und Tätigkeitsaufschlüsselung.",
        bullets: [
          "Mitarbeiter-Spalten zeigen die Verteilung nach Person.",
          "Tätigkeitsspalten sind eine Aufschlüsselung der Gesamtzeit.",
          "Die Tätigkeitsspalten dürfen nicht zusätzlich zur Gesamtzeit addiert werden."
        ]
      },
      {
        title: "Urlaub, Krankheit und Feiertage",
        body: "Genehmigte Fehlzeiten und NRW-Feiertage werden in der Tages- und Monatsberechnung berücksichtigt. Auftragszeitbuchungen sind an vollen Urlaubs- oder Krankheitstagen nicht vorgesehen.",
        bullets: [
          "Fehlzeiten gehören zur arbeitsrechtlichen Zeitbasis.",
          "Auftragszeiten bleiben davon fachlich getrennt.",
          "Unklare Tage sollten vor Monatsabschluss geprüft werden."
        ]
      },
      {
        title: "Was tun bei Fehlern?",
        body: "Fehler bitte nicht durch mehrfaches Buchen korrigieren. Die zuständige Person kann im Backoffice nachvollziehbare Korrekturen erfassen.",
        bullets: [
          "Falsche oder fehlende Mitarbeiterzeit an Buchhaltung oder Admin melden.",
          "Falsche Auftragszeit an Produktionsleitung melden.",
          "Bei Kartenproblemen Ohne Karte anmelden mit Kürzel und eigener PIN nutzen; wiederkehrende Probleme melden.",
          "Bei fehlender Serverantwort erneut anmelden und den Buchungsstatus prüfen. Fehlermeldungen bleiben bis zur nächsten Aktion sichtbar."
        ]
      }
    ],
    []
  );

  return (
    <main className="content">
      <div className="section-heading">
        <div>
          <p>Hilfe und Bedienungsanleitung</p>
          <h1>DZR-R Zeiterfassung</h1>
        </div>
        <div className="manual-actions">
          <IconButton icon={<ArrowLeft />} onClick={onBack} variant="secondary">
            Zurück
          </IconButton>
          <img className="help-logo" src="/logo-vertical.png" alt="DIALOGZENTRUM RHEIN-RUHR GmbH" />
        </div>
      </div>
      <section className="manual-grid">
        {chapters.map((chapter) => (
          <article key={chapter.title} className="manual-card">
            <BookOpen />
            <h2>{chapter.title}</h2>
            <p>{chapter.body}</p>
            <ul>
              {chapter.bullets.map((bullet) => (
                <li key={bullet}>{bullet}</li>
              ))}
            </ul>
          </article>
        ))}
      </section>
    </main>
  );
}


